import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Grid,
  Stack,
  Card,
  CardContent,
  List,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  NotificationsActive as NotificationsActiveIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  Update as UpdateIcon,
  History as HistoryIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { doc, getDoc, collection, getDocs, query, where, orderBy, addDoc, updateDoc, deleteDoc, deleteField, serverTimestamp, Timestamp, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { addDays, differenceInMinutes, differenceInCalendarDays, formatDistanceToNow } from 'date-fns';
import { toDate, formatDateSafe } from '../../shared/utils/dateHelpers';
import { useSnackbar } from '../../shared/utils/snackbar';
import { LoadingSpinner } from '../../shared/components';
import GetAppIcon from '@mui/icons-material/GetApp';
import ClientJourneyOverview from './ClientJourneyOverview';
import ClientSessionsView from './ClientSessionsView';
import ClientCheckInsView from './ClientCheckInsView';
import ClientNotesView from './ClientNotesView';
import { AssessmentSection } from './components/AssessmentSection';
import { EnhancedAssessmentMetrics } from './components/AssessmentMetrics';
import { ClientDetailsHeader } from './components/ClientDetailsHeader';
import { reportRowLabel } from './reportRowLabel';
import { SubmitNominationDialog } from '../success/components/SubmitNominationDialog';
import { ClientNominationsSummary } from '../success/components/ClientNominationsSummary';
import { useClientNominations, useNominationScope } from '../success/hooks/useNominations';
import type { SubmitNominationResult } from '../success/nomination.types';
import {
  LEGACY_PLANNED_COMMENT,
  findFillableRecord,
  latestCountedRecord,
  nextSuccessUpdateDue,
  summarizeSuccessRecord
} from '../../shared/success/successRecords';
import type { SuccessRecordKind } from '../../shared/success/successRecords';
import {
  buildFillPayload,
  buildPlannedPayload,
  buildUpdatePayload,
  suggestTargetsFromAssessments,
} from './utils/successMetricPayloads';
import type { AssessmentTargetSuggestion, SuccessMetricAuthor, SuccessMetricForm } from './utils/successMetricPayloads';

/** Tab keys used for `?tab=` deep links, in the same order as the <Tab> elements. */
const CLIENT_TAB_KEYS = ['overview', 'sessions', 'check-ins', 'notes', 'assessments', 'reports', 'success'] as const;
const CLIENT_TAB_PARAM = 'tab';

const getTabIndexFromKey = (key: string | null): number => {
  if (!key) return 0;
  const index = CLIENT_TAB_KEYS.indexOf(key as (typeof CLIENT_TAB_KEYS)[number]);
  return index >= 0 ? index : 0;
};

interface ClientData {
  id: string;
  client_id: string;
  client_name: string;
  email: string;
  mobile_number: string;
  primary_location: string;
  lead_coach_id: string;
  lead_coach_name: string;
  collaborators: string;
  collaborating_coach_ids?: string[];
  visit_dates: string[];
  visit_history: {
    session_date: string;
    service_type: string;
    pricing_option: string;
    staff_name: string;
    location_name: string;
    duration_minutes: number;
    service_category: string;
    no_show: boolean;
    late_cancel: boolean;
  }[];
  first_session_date: string;
  first_ignition_session_date: string;
  latest_expiration_date: string;
  active_programme: boolean;
  programme_start_date?: string;
  programme_end_date?: string;
  join_date?: string;
  conversion_status?: boolean;
  subsequent_spend?: number;
  pre_health_consult_data_submitted?: boolean;
  health_consult_data_submitted?: boolean;
  health_consult_date?: string;
  client_intake_data_submitted?: boolean;
  upcoming_appointments?: {
    date: {
      value: string;
    };
    coach: string;
    location: string;
  }[];
  current_programme?: string;
}

interface ReportSubmission {
  id: string;
  client_id: string;
  report_type: 'Initial' | 'Review';
  uploadCategory: string;
  uploadDate: string;
  [key: string]: any;
}

interface AssessmentMetric {
  name: string;
  category: 'Physical' | 'Movement' | 'Metabolic' | 'Nutrition';
  initialValue: number | null;
  reviewValue: number | null;
  allReviewValues?: { value: number, date: Date }[];
  unit: string;
  improvement?: number; // Percentage or raw value improvement
  isPositive: boolean; // Whether an increase is positive or negative
}

interface AssessmentCategory {
  name: string;
  metrics: AssessmentMetric[];
}


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ClientNote {
  id: string;
  client_id: string;
  content: string;
  created_by: string;
  created_by_uid: string;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  is_pinned?: boolean;
  category?: string;
  tags?: string[];
  color?: string;
  is_private?: boolean;
  is_important?: boolean;
  is_management_only?: boolean;
  mentions?: string[];
  action_items?: Array<{
    text: string;
    completed: boolean;
    assignee?: string;
  }>;
}

interface GeneratedReport {
  id: string;
  client_id: string;
  report_type: 'Initial' | 'Review' | 'Foundation_Initial' | 'Foundation_Review';
  report_category?: 'lactate_threshold';
  stage?: 'Initial' | 'Review';
  programme?: 'Foundation' | 'Ignition';
  url: string;
  download_url: string;
  generated_at: string;
  report_status?: string;
  created_at?: string;
  is_fallback?: boolean;
  generated_by?: {
    uid: string;
    email: string;
    name: string;
  };
  latest_report?: {
    url: string;
    report_type: string;
    generated_by?: string;
  };
}

// Client Success Metrics interfaces
interface ClientSuccessMetric {
  id: string;
  client_id: string;
  goal: string;               
  goal_update: string;         
  goal_achieved: boolean; 
  target1: string;
  target2: string;
  target3: string;
  target1_update: string;
  target2_update: string;
  target3_update: string;
  target1_achieved: boolean;
  target2_achieved: boolean;
  target3_achieved: boolean;
  comments: string;
  created_by: string;
  created_by_uid: string;
  created_by_email: string;
  updated_at: Date | string;
  created_at: Date | string;
  kind: SuccessRecordKind;
  origin: SuccessRecordKind;
  activity_at: Date | null;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const ClientDetails: React.FC = () => {
  const { userRole, userMetadata, checkAccess, user } = useAuth();
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<ClientData | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(() => getTabIndexFromKey(searchParams.get(CLIENT_TAB_PARAM)));
  const [reportSubmissions, setReportSubmissions] = useState<ReportSubmission[]>([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportGenerationProgress, setReportGenerationProgress] = useState(0);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [generatedReportUrl, setGeneratedReportUrl] = useState('');
  const [showReportSuccessDialog, setShowReportSuccessDialog] = useState(false);
  const [collaboratingCoaches, setCollaboratingCoaches] = useState<{id: string, name: string}[]>([]);
  const [isLoadingCoaches, setIsLoadingCoaches] = useState(false);
  const [clientCheckIns, setClientCheckIns] = useState<{
    twoWeek: { id: string; response: string; submitted_at: string; submitted_by: string }[];
    tenWeek: { id: string; response: string; submitted_at: string; submitted_by: string }[];
  }>({ twoWeek: [], tenWeek: [] });
  const [npsData, setNpsData] = useState<{
    rating: number;
    received: boolean;
    received_date: string;
  } | null>(null);
  const [isLoadingCheckIns, setIsLoadingCheckIns] = useState(false);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [checkInType, setCheckInType] = useState<'2_week' | '10_week'>('2_week');
  const [checkInResponse, setCheckInResponse] = useState('');
  const [selectedCheckInId, setSelectedCheckInId] = useState<string | null>(null);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [checkInToDelete, setCheckInToDelete] = useState<string | null>(null);
  
  const [clientNotes, setClientNotes] = useState<ClientNote[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
// --- Audit log for client details page access (throttled) ---
useEffect(() => {
  if (!user && !userMetadata) return;
  if (!clientId || !client) return;
  const logClientDetailsAccess = async () => {
    const auditUserId = user?.uid || (userMetadata && 'uid' in userMetadata ? userMetadata.uid : undefined);
    const auditUserName = userMetadata && 'displayName' in userMetadata ? userMetadata.displayName : undefined;
    const auditUserEmail = userMetadata && 'email' in userMetadata ? userMetadata.email : undefined;
    if (!auditUserId) return;
    const logKey = `client_details_log_last_${auditUserId}_${clientId}`;
    const lastLog = localStorage.getItem(logKey);
    const now = Date.now();
    if (lastLog && now - parseInt(lastLog, 10) < 3 * 60 * 60 * 1000) return;
    await setDoc(doc(db, 'audit_logs', `client_details_${auditUserId}_${clientId}_${now}`), {
      action: 'Open Client Details',
      actionType: 'ACCESS',
      userId: auditUserId,
      userName: auditUserName || auditUserEmail || 'Unknown',
      userRole: userRole || 'Unknown',
      clientId,
      clientName: client?.client_name || client?.id || 'Unknown',
      timestamp: serverTimestamp(),
      details: `Client Details Accessed for clientId: ${clientId}`
    });
    localStorage.setItem(logKey, now.toString());
  };
  logClientDetailsAccess();
}, [user, userMetadata, userRole, clientId, client]);

  
  // Client Success Metrics state
  const [clientSuccessMetrics, setClientSuccessMetrics] = useState<ClientSuccessMetric[]>([]);
  const [isLoadingSuccessMetrics, setIsLoadingSuccessMetrics] = useState(false);
  const [showSuccessMetricDialog, setShowSuccessMetricDialog] = useState(false);
  const [selectedMetricId, setSelectedMetricId] = useState<string | null>(null);
  const [successMetricTarget1, setSuccessMetricTarget1] = useState('');
  const [successMetricTarget2, setSuccessMetricTarget2] = useState('');
  const [successMetricTarget3, setSuccessMetricTarget3] = useState('');
  const [successMetricTarget1Update, setSuccessMetricTarget1Update] = useState('');
  const [successMetricTarget2Update, setSuccessMetricTarget2Update] = useState('');
  const [successMetricTarget3Update, setSuccessMetricTarget3Update] = useState('');
  const [successMetricTarget1Achieved, setSuccessMetricTarget1Achieved] = useState(false);
  const [successMetricTarget2Achieved, setSuccessMetricTarget2Achieved] = useState(false);
  const [successMetricTarget3Achieved, setSuccessMetricTarget3Achieved] = useState(false);
  const [showNewTarget1Dialog, setShowNewTarget1Dialog] = useState(false);
  const [showNewTarget2Dialog, setShowNewTarget2Dialog] = useState(false);
  const [showNewTarget3Dialog, setShowNewTarget3Dialog] = useState(false);
  const [newTarget1Value, setNewTarget1Value] = useState('');
  const [newTarget2Value, setNewTarget2Value] = useState('');
  const [newTarget3Value, setNewTarget3Value] = useState('');
  const [successMetricComments, setSuccessMetricComments] = useState('');
  const [isSubmittingSuccessMetric, setIsSubmittingSuccessMetric] = useState(false);
  const [successMetricGoal, setSuccessMetricGoal] = useState('');
  const [successMetricGoalUpdate, setSuccessMetricGoalUpdate] = useState('');
  const [successMetricGoalAchieved, setSuccessMetricGoalAchieved] = useState(false);
  const [showNewGoalDialog, setShowNewGoalDialog] = useState(false);
  const [newGoalValue, setNewGoalValue] = useState('');
  const [initialGoal, setInitialGoal] = useState<string | null>(null);
  const [assessmentSuggestion, setAssessmentSuggestion] = useState<AssessmentTargetSuggestion | null>(null);
  const [legacyMetricsExpanded, setLegacyMetricsExpanded] = useState(false);

  // Client Success Nominations state
  const nominationScope = useNominationScope();
  const {
    nominations: clientNominations,
    isLoading: isLoadingNominations,
    error: nominationsError,
    refresh: refreshClientNominations,
  } = useClientNominations(clientId, nominationScope);
  const [showNominationDialog, setShowNominationDialog] = useState(false);

  const handleNominationSubmitted = ({ notificationSent }: SubmitNominationResult) => {
    if (notificationSent) {
      const audience = client?.primary_location
        ? `${client.primary_location} manager notified`
        : 'admins notified';
      enqueueSnackbar(`Nomination submitted - ${audience}`, { variant: 'success' });
    } else {
      enqueueSnackbar(
        'Nomination saved, but the manager notification could not be sent. Please let them know directly.',
        { variant: 'warning', autoHideDuration: 8000 }
      );
    }
    refreshClientNominations();
  };

  // State for generated reports
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);

  // Helper function to get the date of the initial assessment from report submissions
  const getInitialAssessmentDate = (category: string): Date | null => {
    if (!reportSubmissions.length) return null;
    
    const initialSubmissions = reportSubmissions.filter(report => report.report_type === 'Initial');
    
    const submission = initialSubmissions.find(s => 
      s.uploadCategory.toLowerCase().includes(category.toLowerCase())
    );
    
    if (!submission || !submission.timestamp) return null;
    
    return submission.timestamp.toDate();
  };
  // Helper function to extract file ID from Google Drive URL
const extractFileIdFromUrl = (url: string): string => {
  // Handle various Google Drive URL formats
  if (!url) return '';
  
  // Format: https://drive.google.com/file/d/FILE_ID/view
  const filePathMatch = url.match(/\/file\/d\/([^/]+)/);
  if (filePathMatch && filePathMatch[1]) return filePathMatch[1];
  
  // Format: https://drive.google.com/open?id=FILE_ID
  const openIdMatch = url.match(/[?&]id=([^&]+)/);
  if (openIdMatch && openIdMatch[1]) return openIdMatch[1];
  
  // Format: https://docs.google.com/document/d/FILE_ID/edit
  const docsMatch = url.match(/\/document\/d\/([^/]+)/);
  if (docsMatch && docsMatch[1]) return docsMatch[1];
  
  // If no patterns match, fall back to the last segment of the URL
  const segments = url.split('/').filter(segment => segment.length > 0);
  return segments.length > 0 ? segments[segments.length - 1] : '';
};
  // Fetch client notes
  const fetchClientNotes = useCallback(async () => {
    if (!clientId) return;
    
    setIsLoadingNotes(true);
    try {
      const notesQuery = query(
        collection(db, 'client_notes'),
        where('client_id', '==', clientId),
        orderBy('created_at', 'desc')
      );
      
      const notesSnapshot = await getDocs(notesQuery);
      const notesData: ClientNote[] = [];
      
      notesSnapshot.forEach((doc) => {
        const data = doc.data();
        notesData.push({
          id: doc.id,
          client_id: data.client_id,
          content: data.content,
          created_by: data.created_by,
          created_by_uid: data.created_by_uid,
          created_by_email: data.created_by_email,
          created_at: data.created_at instanceof Timestamp 
            ? data.created_at.toDate().toISOString() 
            : data.created_at,
          updated_at: data.updated_at instanceof Timestamp 
            ? data.updated_at.toDate().toISOString() 
            : data.updated_at,
          is_pinned: data.is_pinned || false,
          category: data.category || 'general',
          tags: data.tags || [],
          color: data.color || 'default',
          is_private: data.is_private || false,
          is_important: data.is_important || false,
          is_management_only: data.is_management_only || false,
          mentions: data.mentions || [],
          action_items: data.action_items || []
        });
      });
      
      setClientNotes(notesData);
    } catch (error) {
      enqueueSnackbar('Failed to load client notes', { variant: 'error' });
    } finally {
      setIsLoadingNotes(false);
    }
  }, [clientId, enqueueSnackbar]);

  useEffect(() => {
    const fetchClientDetails = async () => {
      if (!clientId) return;
      
      setLoading(true);
      setError(null);
      try {
        const clientDocRef = doc(db, 'bigquery_cache/client_data/comprehensive_details', clientId);
        const clientDoc = await getDoc(clientDocRef);
        
        if (clientDoc.exists()) {
          const clientData = {
            id: clientDoc.id,
            ...clientDoc.data()
          } as ClientData;
          
          // Get collaborating coach IDs if they exist
          try {
            const coachIdsDocRef = doc(db, `bigquery_cache/client_data/comprehensive_details/${clientId}/collaborating_coach_ids`, 'data');
            const coachIdsDoc = await getDoc(coachIdsDocRef);
            
            if (coachIdsDoc.exists() && coachIdsDoc.data()?.coach_ids) {
              clientData.collaborating_coach_ids = coachIdsDoc.data().coach_ids;
            }
          } catch (coachError) {
            enqueueSnackbar('Error fetching collaborating coach IDs', { variant: 'error' });
          }
          // Check if user has access to this client
          const hasAccess = checkAccess('client', clientData, 'read');
          
          if (hasAccess) {
            setClient(clientData);
            
            // Fetch report submissions
            await fetchReportSubmissions(clientId);
            
            // Fetch generated reports
            await fetchGeneratedReports(clientId);
            
            // Fetch client notes
            fetchClientNotes();
          } else {
            setError('You do not have permission to view this client.');
            setClient(null);
          }
        } else {
          setError('Client not found.');
          setClient(null);
        }
      } catch (error) {
        enqueueSnackbar('Failed to load client details. Please try again.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchClientDetails();
  }, [clientId, checkAccess, fetchClientNotes]);

  // Fetch report submissions for the client
  const fetchReportSubmissions = async (clientId: string) => {
    try {
      const reportsRef = collection(db, 'report_submissions', clientId, 'Initial');
      const reportsSnapshot = await getDocs(reportsRef);
      
      const initialReports: ReportSubmission[] = reportsSnapshot.docs.map(doc => ({
        id: doc.id,
        client_id: clientId,
        report_type: 'Initial',
        ...doc.data()
      } as ReportSubmission));
      
      // Also fetch review reports if they exist
      try {
        const reviewReportsRef = collection(db, 'report_submissions', clientId, 'Review');
        const reviewReportsSnapshot = await getDocs(reviewReportsRef);
        
        const reviewReports: ReportSubmission[] = reviewReportsSnapshot.docs.map(doc => ({
          id: doc.id,
          client_id: clientId,
          report_type: 'Review',
          ...doc.data()
        } as ReportSubmission));
        
        setReportSubmissions([...initialReports, ...reviewReports]);
      } catch (error) {
        setReportSubmissions(initialReports);
      }
    } catch (error) {
      setReportSubmissions([]);
    }
  };

  // Fetch generated reports for the client
  const fetchGeneratedReports = async (clientId: string) => {
    if (!clientId) return;
    
    setIsLoadingReports(true);
    try {
      const reportsRef = collection(db, 'clients', clientId, 'reports');
      const reportsQuery = query(reportsRef, orderBy('generated_at', 'desc'));
      const reportsSnapshot = await getDocs(reportsQuery);
      
      const reports: GeneratedReport[] = reportsSnapshot.docs.map(doc => ({
        id: doc.id,
        client_id: doc.data().client_id || clientId,
        report_type: doc.data().report_type || 'Initial',
        report_category: doc.data().report_category,
        stage: doc.data().stage,
        programme: doc.data().programme,
        url: doc.data().url || '',
        download_url: doc.data().download_url || '',
        generated_at: doc.data().generated_at || new Date().toISOString(),
        report_status: doc.data().report_status,
        created_at: doc.data().created_at,
        generated_by: doc.data().generated_by,
        latest_report: doc.data().latest_report
      }));
      
      setGeneratedReports(reports);
    } catch (error) {
      console.error('Error fetching generated reports:', error);
      setGeneratedReports([]);
    } finally {
      setIsLoadingReports(false);
    }
  };

  // Set up real-time listener for reports
  useEffect(() => {
    if (!clientId) return;
    
    const reportsRef = collection(db, 'clients', clientId, 'reports');
    const reportsQuery = query(reportsRef, orderBy('generated_at', 'desc'));
    
    const unsubscribe = onSnapshot(reportsQuery, (snapshot) => {
      const reports: GeneratedReport[] = snapshot.docs.map(doc => ({
        id: doc.id,
        client_id: doc.data().client_id || clientId,
        report_type: doc.data().report_type || 'Initial',
        report_category: doc.data().report_category,
        url: doc.data().url || '',
        download_url: doc.data().download_url || '',
        generated_at: doc.data().generated_at || new Date().toISOString(),
        report_status: doc.data().report_status,
        created_at: doc.data().created_at,
        generated_by: doc.data().generated_by,
        latest_report: doc.data().latest_report
      }));
      
      setGeneratedReports(reports);
      setIsLoadingReports(false);
    }, (error) => {
      console.error('Error in reports listener:', error);
      setIsLoadingReports(false);
    });
    
    return () => unsubscribe();
  }, [clientId]);

  // Fetch collaborating coaches
  useEffect(() => {
    const fetchCollaboratingCoaches = async () => {
      if (!client?.collaborating_coach_ids?.length) return;
      
      setIsLoadingCoaches(true);
      try {
        const leadCoachId = String(client.lead_coach_id);
        // Filter out the lead coach ID from the collaborating coaches array
        const filteredCoachIds = client.collaborating_coach_ids.filter(
          coachId => coachId !== leadCoachId
        );
        
        if (filteredCoachIds.length === 0) {
          setCollaboratingCoaches([]);
          return;
        }
        
        const coachPromises = filteredCoachIds.map(async (coachId) => {
          const coachDoc = await getDoc(doc(db, 'staff_details', coachId));
          if (coachDoc.exists()) {
            return {
              id: coachId,
              name: coachDoc.data().DisplayName || 'Unknown Coach'
            };
          }
          return { id: coachId, name: 'Unknown Coach' };
        });
        
        const coaches = await Promise.all(coachPromises);
        setCollaboratingCoaches(coaches);
      } catch (error) {
        enqueueSnackbar('Error fetching collaborating coaches', { variant: 'error' });
      } finally {
        setIsLoadingCoaches(false);
      }
    };
    
    fetchCollaboratingCoaches();
  }, [client?.collaborating_coach_ids]);

  // Generate assessment report
  const generateAssessmentReport = async (reportType: 'Initial' | 'Review') => {
    if (!clientId) {
      enqueueSnackbar('Client ID is required', { variant: 'error' });
      return;
    }
    
    if (!user || !user.uid) {
      enqueueSnackbar('You must be logged in to generate reports', { variant: 'error' });
      return;
    }
    
    setIsGeneratingReport(true);
    setReportGenerationProgress(10);
    setShowReportDialog(true);
    
    // Start the progress animation with dynamic speed
    let progressStartTime = Date.now();
    const progressInterval = setInterval(() => {
      setReportGenerationProgress(prev => {
        const elapsed = (Date.now() - progressStartTime) / 1000; // seconds elapsed
        
        // Dynamic progress based on elapsed time
        if (elapsed < 30) {
          // First 30 seconds: reach 50%
          return Math.min(50, prev + 2);
        } else if (elapsed < 60) {
          // 30-60 seconds: reach 70%
          return Math.min(70, prev + 1);
        } else if (elapsed < 120) {
          // 60-120 seconds: reach 85%
          return Math.min(85, prev + 0.5);
        } else {
          // After 2 minutes: slow progress to 88%
          return Math.min(88, prev + 0.1);
        }
      });
    }, 1000); // Update every second
    
    try {
      // Check if user document exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      // If user document doesn't exist, create it
      if (!userDoc.exists()) {
        console.log('Creating user document for logged in user');
        try {
          await setDoc(userDocRef, {
            email: user.email || '',
            displayName: user.displayName || '',
            photoURL: user.photoURL || '',
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
            role: 'user' // Default role
          });
          console.log('User document created successfully');
        } catch (createUserError) {
          console.error('Error creating user document:', createUserError);
          // Continue with report generation even if user document creation fails
        }
      }
      
      // Make POST request to generate report
      try {
        console.log('Sending report generation request with data:', {
          client_id: clientId,
          report_type: reportType,
          user_id: user.uid,
          user_email: user.email || '',
          user_name: user.displayName || ''
        });
        
        // Use our Cloud Function instead of directly calling Google Apps Script
        const response = await fetch('https://us-central1-ufit-client-tracker.cloudfunctions.net/generateReport', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: clientId,
            report_type: reportType,
            user_id: user.uid,
            user_email: user.email || '',
            user_name: user.displayName || ''
          })
        });
        
        // Set progress to 90% when we get a response
        setReportGenerationProgress(90);
        
        // Log the response status
        console.log('Report generation response status:', response.status, response.statusText);
        
        if (!response.ok) {
          let errorMessage = 'Failed to generate report';
          try {
            const errorText = await response.text();
            console.error('Error response text:', errorText);
            errorMessage = `Failed to generate report: ${errorText || response.statusText}`;
          } catch (textError) {
            console.error('Error reading response text:', textError);
          }
          throw new Error(errorMessage);
        }
        
        let data;
        try {
          const responseText = await response.text();
          console.log('Response text:', responseText);
          
          // Try to parse the response as JSON
          try {
            data = JSON.parse(responseText);
          } catch (parseError) {
            console.error('Error parsing JSON response:', parseError);
            throw new Error('Invalid JSON response from report generation service');
          }
        } catch (responseError) {
          console.error('Error reading response:', responseError);
          throw new Error('Failed to read response from report generation service');
        }
        
        console.log('Parsed response data:', data);
        
        if (!data || !data.url) {
          console.error('Invalid response data:', data);
          throw new Error('Invalid response from report generation service: missing URL');
        }
        
        // Set progress to 95%
        setReportGenerationProgress(95);
        
        // Store the report URL
        setGeneratedReportUrl(data.url);
        
        // Save to Firestore
        try {
          const reportsRef = collection(db, 'clients', clientId, 'reports');
          await addDoc(reportsRef, {
            client_id: clientId,
            report_type: reportType,
            url: data.url,
            download_url: `https://drive.google.com/uc?export=download&id=${extractFileIdFromUrl(data.url)}`,
            generated_at: new Date().toISOString(),
            report_status: 'Generated',
            created_at: new Date().toISOString(),
            generated_by: {
              uid: user.uid,
              email: user.email || '',
              name: user.displayName || ''
            },
            latest_report: {
              url: data.url,
              report_type: reportType,
              generated_by: user.displayName || user.email || 'Unknown'
            }
          });
          
          // The reports list will be automatically updated by the onSnapshot listener
          setReportGenerationProgress(100);
          
          // Close the progress dialog and show the success dialog
          setTimeout(() => {
            setShowReportDialog(false);
            setShowReportSuccessDialog(true);
          }, 1000);
          
          enqueueSnackbar('Report generated successfully!', { variant: 'success' });
        } catch (firestoreError) {
          console.error('Error saving report to Firestore:', firestoreError);
          enqueueSnackbar('Report generated but failed to save. Please try again.', { variant: 'warning' });
          setReportGenerationProgress(0);
        }
      } catch (fetchError) {
        console.error('Error generating report:', fetchError);
        
        // Check if it's a timeout error
        // Fallback: Generate a mock report URL if the API call fails
        try {
          console.log('Using fallback report generation mechanism');
          
          // Create a fallback report URL (this would normally come from the API)
          const fallbackReportUrl = `https://docs.google.com/document/d/fallback-${clientId}-${reportType}-${Date.now()}/edit`;
          console.log('Generated fallback report URL:', fallbackReportUrl);
          
          // Store the fallback report URL
          setGeneratedReportUrl(fallbackReportUrl);
          
          // Save to Firestore with fallback data
          const reportsRef = collection(db, 'clients', clientId, 'reports');
          await addDoc(reportsRef, {
            client_id: clientId,
            report_type: reportType,
            url: fallbackReportUrl,
            download_url: fallbackReportUrl,
            generated_at: new Date().toISOString(),
            report_status: 'Generated (Fallback)',
            created_at: new Date().toISOString(),
            generated_by: {
              uid: user.uid,
              email: user.email || '',
              name: user.displayName || ''
            },
            latest_report: {
              url: fallbackReportUrl,
              report_type: reportType,
              generated_by: user.displayName || user.email || 'Unknown'
            },
            is_fallback: true
          });
          
          setReportGenerationProgress(100);
          
          // Close the progress dialog and show the success dialog
          setTimeout(() => {
            setShowReportDialog(false);
            setShowReportSuccessDialog(true);
          }, 1000);
          
          enqueueSnackbar('Report generated with fallback mechanism. Some features may be limited.', { variant: 'warning' });
        } catch (fallbackError) {
          console.error('Error in fallback report generation:', fallbackError);
          enqueueSnackbar(`Failed to generate report: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`, { variant: 'error' });
          setReportGenerationProgress(0);
        }
      } finally {
        clearInterval(progressInterval);
        setIsGeneratingReport(false);
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Error in report generation process:', error);
      setReportGenerationProgress(0);
      setIsGeneratingReport(false);
      enqueueSnackbar('Failed to generate report. Please try again.', { variant: 'error' });
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const nextParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      nextParams.delete(CLIENT_TAB_PARAM);
    } else {
      nextParams.set(CLIENT_TAB_PARAM, CLIENT_TAB_KEYS[newValue]);
    }
    setSearchParams(nextParams, { replace: true });
  };

  // Follow `?tab=` changes made outside the tab bar (notifications, nominations table, back button).
  useEffect(() => {
    const requestedTab = getTabIndexFromKey(searchParams.get(CLIENT_TAB_PARAM));
    setTabValue(current => (current === requestedTab ? current : requestedTab));
  }, [searchParams]);

  const canEditClient = userRole === 'ADMIN' || 
    userRole === 'MANAGER' || 
    (userRole === 'USER' && client?.lead_coach_id === userMetadata?.coach_id);

  // Helper functions to get assessment dates from visit history
  const getAssessmentDate = (assessmentType: string): string | null => {
    if (!client?.visit_history?.length) return null;
    
    // Find the earliest visit that matches the assessment type
    const matchingVisits = client.visit_history.filter(visit => {
      if (assessmentType === 'metabolic') {
        return visit.pricing_option.includes('Metabolic and Cardiorespiratory Assessment');
      } else if (assessmentType === 'performance') {
        return visit.pricing_option.includes('Physical Performance Screen');
      } else if (assessmentType === 'nutrition') {
        return visit.pricing_option.includes('Nutrition');
      }
      return false;
    });
    
    // Sort by date and return the earliest one
    if (matchingVisits.length > 0) {
      return [...matchingVisits].sort((a, b) => {
        const dateA = toDate(a.session_date);
        const dateB = toDate(b.session_date);
        if (!dateA || !dateB) return 0;
        return dateA.getTime() - dateB.getTime();
      })[0].session_date;
    }
    
    return null;
  };

  // Calculate check-in dates based on first attendance
  const getCheckInDates = (): { twoWeek: Date | null, sixWeekNPS: Date | null, tenWeek: Date | null } => {
    if (!client?.visit_dates?.length) return { twoWeek: null, sixWeekNPS: null, tenWeek: null };
    
    const firstAttendanceDate = toDate(client?.first_ignition_session_date);
    if (!firstAttendanceDate) return { twoWeek: null, sixWeekNPS: null, tenWeek: null };
    
    const firstAttendance = firstAttendanceDate;
    
    return {
      twoWeek: addDays(firstAttendance, 14),
      sixWeekNPS: addDays(firstAttendance, 42), // 6 weeks = 42 days
      tenWeek: addDays(firstAttendance, 70) // 10 weeks = 70 days
    };
  };

  // Helper function to get sorted timeline milestones
  const getSortedTimelineMilestones = () => {
    const milestones: Array<{
      date: Date | null;
      label: string;
      color: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'grey';
      show: boolean;
    }> = [];

    // Health Consult
    if (client?.health_consult_data_submitted && client?.health_consult_date) {
      const consultDate = toDate(client.health_consult_date);
      if (consultDate) {
        milestones.push({
          date: consultDate,
          label: 'Health Consult',
          color: 'secondary',
          show: true
        });
      }
    }

    // Joined UFIT
    if (client?.visit_dates && client.visit_dates.length > 0) {
      const sortedDates = [...client.visit_dates].sort();
      const firstVisitDate = toDate(sortedDates[0]);
      if (firstVisitDate) {
        milestones.push({
          date: firstVisitDate,
          label: 'Joined UFIT',
          color: 'primary',
          show: true
        });
      }
    }

    // Programme Started
    if (client?.first_ignition_session_date) {
      const programStartDate = toDate(client.first_ignition_session_date);
      if (programStartDate) {
        milestones.push({
          date: programStartDate,
          label: 'Programme Started',
          color: 'primary',
          show: true
        });
      }
    } else if (client?.first_session_date) {
      const firstSessionDate = toDate(client.first_session_date);
      if (firstSessionDate) {
        milestones.push({
          date: firstSessionDate,
          label: 'Programme Started',
          color: 'primary',
          show: true
        });
      }
    }

    // First Attendance
    if (client?.visit_dates && client.visit_dates.length > 0) {
      const sortedVisitDates = [...client.visit_dates].sort();
      const firstAttendanceDate = toDate(sortedVisitDates[0]);
      if (firstAttendanceDate) {
        milestones.push({
          date: firstAttendanceDate,
          label: 'First Attendance',
          color: 'success',
          show: true
        });
      }
    }

    // Initial Metabolic Assessment
    const metabolicDate = getAssessmentDate('metabolic');
    if (metabolicDate) {
      const metabolicAssessmentDate = toDate(metabolicDate);
      if (metabolicAssessmentDate) {
        milestones.push({
          date: metabolicAssessmentDate,
          label: 'Initial Metabolic Assessment',
          color: 'warning',
          show: true
        });
      }
    }

    // Initial Performance Screening
    const performanceDate = getAssessmentDate('performance');
    if (performanceDate) {
      const performanceAssessmentDate = toDate(performanceDate);
      if (performanceAssessmentDate) {
        milestones.push({
          date: performanceAssessmentDate,
          label: 'Initial Performance Screening',
          color: 'warning',
          show: true
        });
      }
    }

    // Initial Nutrition Assessment
    const nutritionDate = getAssessmentDate('nutrition');
    if (nutritionDate) {
      const nutritionAssessmentDate = toDate(nutritionDate);
      if (nutritionAssessmentDate) {
        milestones.push({
          date: nutritionAssessmentDate,
          label: 'Initial Nutrition Assessment',
          color: 'warning',
          show: true
        });
      }
    }

    // Check-in dates
    if (client?.visit_dates && client.visit_dates.length > 0) {
      const checkInDates = getCheckInDates();
      
      // 2-week check-in
      if (checkInDates.twoWeek) {
        milestones.push({
          date: checkInDates.twoWeek,
          label: '2-Week Check-in',
          color: clientCheckIns.twoWeek.length > 0 ? 'success' : 'grey',
          show: true
        });
      }
      
      // 6-week NPS
      if (checkInDates.sixWeekNPS) {
        milestones.push({
          date: checkInDates.sixWeekNPS,
          label: '6-Week NPS',
          color: npsData?.received ? 'success' : 'grey',
          show: true
        });
      }
      
      // 10-week check-in
      if (checkInDates.tenWeek) {
        milestones.push({
          date: checkInDates.tenWeek,
          label: '10-Week Check-in',
          color: clientCheckIns.tenWeek.length > 0 ? 'success' : 'grey',
          show: true
        });
      }
    }

    // Session milestones (every 25 sessions)
    if (client?.visit_dates && client.visit_dates.length > 0) {
      const totalSessions = client.visit_dates.length;
      const sessionMilestones = [25, 50, 75, 100, 125, 150, 175, 200, 225, 250, 275, 300];
      
      sessionMilestones.forEach(milestone => {
        if (totalSessions >= milestone) {
          // Get the date of the milestone session
          const sortedVisitDates = [...client.visit_dates].sort();
          const milestoneDate = toDate(sortedVisitDates[milestone - 1]);
          if (milestoneDate) {
            milestones.push({
              date: milestoneDate,
              label: `${milestone} Sessions Attended`,
              color: 'warning',
              show: true
            });
          }
        }
      });
    }

    // Latest Session
    if (client?.visit_dates && client.visit_dates.length > 0) {
      const latestSessionDate = toDate(client.visit_dates[client.visit_dates.length - 1]);
      if (latestSessionDate) {
        milestones.push({
          date: latestSessionDate,
          label: 'Latest Session',
          color: 'info',
          show: true
        });
      }
    }

    // Programme End
    if (client?.latest_expiration_date) {
      const programEndDate = toDate(client.latest_expiration_date);
      if (programEndDate) {
        milestones.push({
          date: programEndDate,
          label: 'Programme End',
          color: 'primary',
          show: true
        });
      }
    }

    // Sort milestones by date
    return milestones
      .filter(m => m.show && m.date !== null)
      .sort((a, b) => a.date!.getTime() - b.date!.getTime());
  };

  // Helper function to check if a report is submitted
  const isReportSubmitted = (category: string, reportType: 'Initial' | 'Review' | 'Any' = 'Any'): boolean => {
    if (!reportSubmissions.length) return false;
    
    return reportSubmissions.some(report => {
      const categoryMatch = report.uploadCategory.toLowerCase().includes(category.toLowerCase());
      const typeMatch = reportType === 'Any' || report.report_type === reportType;
      return categoryMatch && typeMatch;
    });
  };
  
  // Check if all reports are submitted for a specific report type
  const areAllReportsSubmitted = (reportType: 'Initial' | 'Review'): boolean => {
    // Check pre-assessment data first
    if (reportType === 'Initial') {
      // Return false if client is null or any required pre-assessment data is missing
      if (!client || 
          !client.health_consult_data_submitted || 
          !client.client_intake_data_submitted) {
        return false;
      }
    }
    
    // For Initial and Review reports, we need Inbody, Movement, Metabolic, Nutrition, Training Plan
    const requiredReports = [
      'inbody',
      'movement',
      'metabolic',
      'nutrition',
      'training'
    ];
    
    return requiredReports.every(report => isReportSubmitted(report, reportType));
  };

  const extractAssessmentMetrics = (): AssessmentCategory[] => {
    if (!reportSubmissions.length) return [];

    // Group submissions by type (Initial or Review)
    const initialSubmissions = reportSubmissions.filter(report => report.report_type === 'Initial');
    const reviewSubmissions = reportSubmissions.filter(report => report.report_type === 'Review');

    // Sort review submissions by timestamp (newest first)
    const sortedReviewSubmissions = [...reviewSubmissions].sort((a, b) => {
      const aTime = a.timestamp ? a.timestamp.seconds : 0;
      const bTime = b.timestamp ? b.timestamp.seconds : 0;
      return bTime - aTime; 
    });

    // Helper function to get all review values chronologically
    const getAllReviewValues = (category: string, field: string): { value: number, date: Date }[] => {
      const relevantSubmissions = reviewSubmissions.filter(s => 
        s.uploadCategory.toLowerCase().includes(category.toLowerCase())
      );
      
      return relevantSubmissions
        .map(submission => {
          if (!submission || !submission.formData) return null;
          
          const formData = typeof submission.formData === 'string' 
            ? JSON.parse(submission.formData) 
            : submission.formData;
          
          const value = formData[field];
          if (!value) return null;
          
          // Try to convert to number
          const numValue = parseFloat(value);
          if (isNaN(numValue)) return null;
          
          return {
            value: numValue,
            date: submission.timestamp ? submission.timestamp.toDate() : new Date()
          };
        })
        .filter((item): item is { value: number, date: Date } => item !== null)
        .sort((a, b) => a.date.getTime() - b.date.getTime()); 
    };

    // Helper function to extract value from form data
    const extractValue = (submissions: ReportSubmission[], category: string, field: string): number | null => {
      const submission = submissions.find(s => 
        s.uploadCategory.toLowerCase().includes(category.toLowerCase())
      );
      
      if (!submission || !submission.formData) return null;
      
      const formData = typeof submission.formData === 'string' 
        ? JSON.parse(submission.formData) 
        : submission.formData;
      
      const value = formData[field];
      if (!value) return null;
      
      // Try to convert to number
      const numValue = parseFloat(value);
      return isNaN(numValue) ? null : numValue;
    };

    // Helper function to get latest review value
    const getLatestReviewValue = (category: string, field: string): number | null => {
      if (sortedReviewSubmissions.length === 0) return null;
      
      const submission = sortedReviewSubmissions.find(s => 
        s.uploadCategory.toLowerCase().includes(category.toLowerCase())
      );
      
      if (!submission || !submission.formData) return null;
      
      const formData = typeof submission.formData === 'string' 
        ? JSON.parse(submission.formData) 
        : submission.formData;
      
      const value = formData[field];
      if (!value) return null;
      
      // Try to convert to number
      const numValue = parseFloat(value);
      return isNaN(numValue) ? null : numValue;
    };

    // Helper function to get the date of the initial assessment
    const getInitialAssessmentDate = (category: string): Date | null => {
      const submission = initialSubmissions.find(s => 
        s.uploadCategory.toLowerCase().includes(category.toLowerCase())
      );
      
      if (!submission || !submission.timestamp) return null;
      
      return submission.timestamp.toDate();
    };

    // Physical Metrics (InBody Scan)
    const physicalMetrics: AssessmentMetric[] = [
      {
        name: 'Weight',
        category: 'Physical',
        initialValue: extractValue(initialSubmissions, 'inbody', 'Weight'),
        reviewValue: getLatestReviewValue('inbody', 'Weight'),
        allReviewValues: getAllReviewValues('inbody', 'Weight'),
        unit: 'kg',
        isPositive: false // Decrease is positive
      },
      {
        name: 'Body Fat Percentage',
        category: 'Physical',
        initialValue: extractValue(initialSubmissions, 'inbody', 'Body Fat Percentage'),
        reviewValue: getLatestReviewValue('inbody', 'Body Fat Percentage'),
        allReviewValues: getAllReviewValues('inbody', 'Body Fat Percentage'),
        unit: '%',
        isPositive: false // Decrease is positive
      },
      {
        name: 'Skeletal Muscle Mass',
        category: 'Physical',
        initialValue: extractValue(initialSubmissions, 'inbody', 'Skeletal Muscle Mass'),
        reviewValue: getLatestReviewValue('inbody', 'Skeletal Muscle Mass'),
        allReviewValues: getAllReviewValues('inbody', 'Skeletal Muscle Mass'),
        unit: 'kg',
        isPositive: true // Increase is positive
      },
      {
        name: 'Visceral Fat',
        category: 'Physical',
        initialValue: extractValue(initialSubmissions, 'inbody', 'Visceral Fat'),
        reviewValue: getLatestReviewValue('inbody', 'Visceral Fat'),
        allReviewValues: getAllReviewValues('inbody', 'Visceral Fat'),
        unit: 'level',
        isPositive: false // Decrease is positive
      },
      {
        name: 'Height',
        category: 'Physical',
        initialValue: extractValue(initialSubmissions, 'inbody', 'Height'),
        reviewValue: getLatestReviewValue('inbody', 'Height'),
        allReviewValues: getAllReviewValues('inbody', 'Height'),
        unit: 'cm',
        isPositive: true // Increase is positive
      }
    ];

    // Movement Metrics
    const movementMetrics: AssessmentMetric[] = [
      {
        name: 'Squat Peak Force (Total)',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'Squat Peak Force (Total)'),
        reviewValue: getLatestReviewValue('movement', 'Squat Peak Force (Total)'),
        allReviewValues: getAllReviewValues('movement', 'Squat Peak Force (Total)'),
        unit: 'N',
        isPositive: true // Increase is positive
      },
      {
        name: 'Squat Asymmetry',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'Squat Asymmetry'),
        reviewValue: getLatestReviewValue('movement', 'Squat Asymmetry'),
        allReviewValues: getAllReviewValues('movement', 'Squat Asymmetry'),
        unit: '%',
        isPositive: false // Decrease is positive
      },
      {
        name: 'CMJ Jump Height',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'CMJ Jump Height'),
        reviewValue: getLatestReviewValue('movement', 'CMJ Jump Height'),
        allReviewValues: getAllReviewValues('movement', 'CMJ Jump Height'),
        unit: 'cm',
        isPositive: true // Increase is positive
      },
      {
        name: 'Grip Peak Force (Left)',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'Grip Peak Force (Left)'),
        reviewValue: getLatestReviewValue('movement', 'Grip Peak Force (Left)'),
        allReviewValues: getAllReviewValues('movement', 'Grip Peak Force (Left)'),
        unit: 'kg',
        isPositive: true // Increase is positive
      },
      {
        name: 'Grip Peak Force (Right)',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'Grip Peak Force (Right)'),
        reviewValue: getLatestReviewValue('movement', 'Grip Peak Force (Right)'),
        allReviewValues: getAllReviewValues('movement', 'Grip Peak Force (Right)'),
        unit: 'kg',
        isPositive: true // Increase is positive
      },
      {
        name: 'Grip Asymmetry',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'Grip Asymmetry'),
        reviewValue: getLatestReviewValue('movement', 'Grip Asymmetry'),
        allReviewValues: getAllReviewValues('movement', 'Grip Asymmetry'),
        unit: '%',
        isPositive: false // Decrease is positive
      },
      {
        name: 'Shoulder Flexion Asymmetry',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'Shoulder Flexion Asymmetry'),
        reviewValue: getLatestReviewValue('movement', 'Shoulder Flexion Asymmetry'),
        allReviewValues: getAllReviewValues('movement', 'Shoulder Flexion Asymmetry'),
        unit: '%',
        isPositive: false // Decrease is positive
      },
      {
        name: 'Isometric Mid Thigh Pull Peak Vertical Force (Total)',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'Isometric Mid Thigh Pull Peak Vertical Force (Total)'),
        reviewValue: getLatestReviewValue('movement', 'Isometric Mid Thigh Pull Peak Vertical Force (Total)'),
        allReviewValues: getAllReviewValues('movement', 'Isometric Mid Thigh Pull Peak Vertical Force (Total)'),
        unit: 'N',
        isPositive: true // Increase is positive
      },
      {
        name: 'Isometric Bench Press Peak Vertical Force (Total)',
        category: 'Movement',
        initialValue: extractValue(initialSubmissions, 'movement', 'Isometric Bench Press Peak Vertical Force (Total)'),
        reviewValue: getLatestReviewValue('movement', 'Isometric Bench Press Peak Vertical Force (Total)'),
        allReviewValues: getAllReviewValues('movement', 'Isometric Bench Press Peak Vertical Force (Total)'),
        unit: 'N',
        isPositive: true // Increase is positive
      }
    ];

    // Metabolic Metrics
    const metabolicMetrics: AssessmentMetric[] = [
      {
        name: 'Resting Heart Rate',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Resting Heart Rate'),
        reviewValue: getLatestReviewValue('metabolic', 'Resting Heart Rate'),
        allReviewValues: getAllReviewValues('metabolic', 'Resting Heart Rate'),
        unit: 'bpm',
        isPositive: false // Decrease is positive
      },
      {
        name: 'Heart Rate Variability',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Heart Rate Variability'),
        reviewValue: getLatestReviewValue('metabolic', 'Heart Rate Variability'),
        allReviewValues: getAllReviewValues('metabolic', 'Heart Rate Variability'),
        unit: 'ms',
        isPositive: true // Increase is positive
      },
      {
        name: 'Resting Metabolic Rate',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Resting Metabolic Rate'),
        reviewValue: getLatestReviewValue('metabolic', 'Resting Metabolic Rate'),
        allReviewValues: getAllReviewValues('metabolic', 'Resting Metabolic Rate'),
        unit: 'kcal',
        isPositive: true // Increase is positive
      },
      {
        name: 'VO2max',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'VO2max'),
        reviewValue: getLatestReviewValue('metabolic', 'VO2max'),
        allReviewValues: getAllReviewValues('metabolic', 'VO2max'),
        unit: 'ml/kg/min',
        isPositive: true // Increase is positive
      },
      {
        name: 'Max Speed',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Max Speed'),
        reviewValue: getLatestReviewValue('metabolic', 'Max Speed'),
        allReviewValues: getAllReviewValues('metabolic', 'Max Speed'),
        unit: 'km/h',
        isPositive: true // Increase is positive
      },
      {
        name: 'Max Watts',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Max Watts'),
        reviewValue: getLatestReviewValue('metabolic', 'Max Watts'),
        allReviewValues: getAllReviewValues('metabolic', 'Max Watts'),
        unit: 'W',
        isPositive: true // Increase is positive
      },
      {
        name: 'Max Heart Rate',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Max Heart Rate'),
        reviewValue: getLatestReviewValue('metabolic', 'Max Heart Rate'),
        allReviewValues: getAllReviewValues('metabolic', 'Max Heart Rate'),
        unit: 'bpm',
        isPositive: true // Increase is positive
      },
      {
        name: 'Ventilation',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Ventilation'),
        reviewValue: getLatestReviewValue('metabolic', 'Ventilation'),
        allReviewValues: getAllReviewValues('metabolic', 'Ventilation'),
        unit: 'L/min',
        isPositive: true // Increase is positive
      },
      {
        name: 'Tidal Volume',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Tidal Volume'),
        reviewValue: getLatestReviewValue('metabolic', 'Tidal Volume'),
        allReviewValues: getAllReviewValues('metabolic', 'Tidal Volume'),
        unit: 'L',
        isPositive: true // Increase is positive
      },
      {
        name: 'Respiratory Frequency',
        category: 'Metabolic',
        initialValue: extractValue(initialSubmissions, 'metabolic', 'Respiratory Frequency'),
        reviewValue: getLatestReviewValue('metabolic', 'Respiratory Frequency'),
        allReviewValues: getAllReviewValues('metabolic', 'Respiratory Frequency'),
        unit: 'breaths/min',
        isPositive: true // Increase is positive
      }
    ];

    // Nutrition Metrics
    const nutritionMetrics: AssessmentMetric[] = [
      {
        name: 'Calories',
        category: 'Nutrition',
        initialValue: extractValue(initialSubmissions, 'nutrition', 'Calories'),
        reviewValue: getLatestReviewValue('nutrition', 'Calories'),
        allReviewValues: getAllReviewValues('nutrition', 'Calories'),
        unit: 'kcal',
        isPositive: true // Increase is positive
      },
      {
        name: 'Fluid',
        category: 'Nutrition',
        initialValue: extractValue(initialSubmissions, 'nutrition', 'Fluid'),
        reviewValue: getLatestReviewValue('nutrition', 'Fluid'),
        allReviewValues: getAllReviewValues('nutrition', 'Fluid'),
        unit: 'L',
        isPositive: true // Increase is positive
      },
      {
        name: 'Protein',
        category: 'Nutrition',
        initialValue: extractValue(initialSubmissions, 'nutrition', 'Protein'),
        reviewValue: getLatestReviewValue('nutrition', 'Protein'),
        allReviewValues: getAllReviewValues('nutrition', 'Protein'),
        unit: 'g',
        isPositive: true // Increase is positive
      }
    ];

    // Calculate improvement percentages for metrics that have both initial and review values
    const calculateImprovements = (metrics: AssessmentMetric[]): AssessmentMetric[] => {
      return metrics.map(metric => {
        if (metric.initialValue !== null && metric.reviewValue !== null) {
          const change = metric.reviewValue - metric.initialValue;
          const percentChange = (change / Math.abs(metric.initialValue)) * 100;
          return {
            ...metric,
            improvement: percentChange
          };
        }
        return metric;
      });
    };

    // Apply improvement calculations to all metric categories
    const physicalMetricsWithImprovements = calculateImprovements(physicalMetrics);
    const movementMetricsWithImprovements = calculateImprovements(movementMetrics);
    const metabolicMetricsWithImprovements = calculateImprovements(metabolicMetrics);
    const nutritionMetricsWithImprovements = calculateImprovements(nutritionMetrics);

    // Return all categories
    return [
      {
        name: 'Physical',
        metrics: physicalMetricsWithImprovements
      },
      {
        name: 'Movement',
        metrics: movementMetricsWithImprovements
      },
      {
        name: 'Metabolic',
        metrics: metabolicMetricsWithImprovements
      },
      {
        name: 'Nutrition',
        metrics: nutritionMetricsWithImprovements
      }
    ];
  };

  // Helper function to get user display name
  const getUserDisplayName = async (uid: string): Promise<string> => {
    if (!uid) return 'Unknown User';
    
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // Prioritize display_name over email
        if (userData.display_name && userData.display_name.trim() !== '') {
          return userData.display_name;
        }
        // Fallback to email if no display name
        if (userData.email) {
          return userData.email;
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
    
    // Last resort: return the UID
    return uid;
  };

  // Fetch client check-ins
  const fetchClientCheckIns = async () => {
    if (!clientId) return;
    
    setIsLoadingCheckIns(true);
    try {
      // Fetch check-ins from client_check_ins collection
      const checkInsQuery = query(
        collection(db, 'client_check_ins'),
        where('client_id', '==', clientId),
        orderBy('submitted_at', 'desc')
      );
      
      const checkInsSnapshot = await getDocs(checkInsQuery);
      
      const twoWeekCheckIns: any[] = [];
      const tenWeekCheckIns: any[] = [];
      
      await Promise.all(checkInsSnapshot.docs.map(async (doc) => {
        const data = doc.data();
        
        // Get display name - use stored name if available, otherwise fetch
        let displayName = data.submitted_by_name;
        if (!displayName && data.submitted_by) {
          displayName = await getUserDisplayName(data.submitted_by);
        }
        
        const checkIn = {
          id: doc.id,
          response: data.response || '',
          submitted_at: data.submitted_at?.toDate?.() 
            ? data.submitted_at.toDate().toISOString() 
            : data.submitted_at || '',
          submitted_by: displayName || 'Unknown User',
          check_in_type: data.check_in_type
        };
        
        if (data.check_in_type === '2_week') {
          twoWeekCheckIns.push(checkIn);
        } else if (data.check_in_type === '10_week') {
          tenWeekCheckIns.push(checkIn);
        }
      }));
      
      setClientCheckIns({
        twoWeek: twoWeekCheckIns,
        tenWeek: tenWeekCheckIns
      });
      
      // Fetch NPS data from comprehensive_details
      const npsDocRef = doc(db, 'bigquery_cache/client_data/comprehensive_details', clientId);
      const npsDocSnap = await getDoc(npsDocRef);
      
      if (npsDocSnap.exists()) {
        const data = npsDocSnap.data();
        setNpsData({
          rating: data.nps_rating || 0,
          received: data.nps_received || false,
          received_date: data.nps_received_date?.value || ''
        });
      }
      
    } catch (error) {
      enqueueSnackbar('Error fetching client check-ins', { variant: 'error' });
    } finally {
      setIsLoadingCheckIns(false);
    }
  };

  // Handle check-in submission
  const handleCheckInSubmit = async () => {
    if (!clientId || !checkInResponse.trim()) return;
    
    try {
      const checkInData = {
        client_id: clientId,
        check_in_type: checkInType,
        response: checkInResponse.trim(),
        submitted_by: user?.uid || '',
        submitted_by_name: user?.displayName && user.displayName.trim() !== '' 
          ? user.displayName 
          : user?.email || 'Unknown User',
        submitted_at: serverTimestamp()
      };
      
      if (selectedCheckInId) {
        // Update existing check-in
        await updateDoc(doc(db, 'client_check_ins', selectedCheckInId), checkInData);
      } else {
        // Create new check-in
        await addDoc(collection(db, 'client_check_ins'), checkInData);
        
        // Notification fan-out handled by Cloud Function trigger
      }
      
      // Reset form and fetch updated data
      setCheckInResponse('');
      setSelectedCheckInId(null);
      setShowCheckInDialog(false);
      fetchClientCheckIns();
      
      enqueueSnackbar(selectedCheckInId ? 'Check-in updated successfully' : 'Check-in submitted successfully', { variant: 'success' });
      
    } catch (error) {
      enqueueSnackbar('Error submitting check-in', { variant: 'error' });
    }
  };

  // Handle check-in deletion
  const handleCheckInDelete = async (checkInId: string) => {
    if (!checkInId) return;
    
    try {
      await deleteDoc(doc(db, 'client_check_ins', checkInId));
      fetchClientCheckIns();
    } catch (error) {
      enqueueSnackbar('Error deleting check-in', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchClientCheckIns();
  }, [clientId]);


  // Handle note pinning/unpinning
  const handleToggleNotePin = async (noteId: string, currentPinState: boolean) => {
    if (!noteId) return;
    
    try {
      await updateDoc(doc(db, 'client_notes', noteId), {
        is_pinned: !currentPinState
      });
      
      // Update local state
      setClientNotes(prevNotes => 
        prevNotes.map(note => 
          note.id === noteId 
            ? { ...note, is_pinned: !currentPinState } 
            : note
        )
      );
      
      enqueueSnackbar(
        `Note ${!currentPinState ? 'pinned' : 'unpinned'} successfully`, 
        { variant: 'success' }
      );
    } catch (error) {
      enqueueSnackbar('Failed to update note pin status', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchClientNotes();
  }, [clientId]);

  const fetchClientSuccessMetrics = useCallback(async () => {
    if (!clientId) return;
    
    setIsLoadingSuccessMetrics(true);
    try {
      // First, query the client_success collection
      const q = query(
        collection(db, 'client_success'),
        where('client_id', '==', clientId),
        orderBy('created_at', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const metrics: ClientSuccessMetric[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Classify the raw doc: its stored `id` field (if any) is the assessment-seed fingerprint
        const summary = summarizeSuccessRecord(doc.id, data);
        const createdAt = toDate(data.created_at) || new Date();
        const updatedAt = toDate(data.updated_at) || createdAt;
        metrics.push({
          id: doc.id,
          client_id: data.client_id,
          goal: data.goal || '',
          goal_update: data.goal_update || '',
          goal_achieved: data.goal_achieved || false,
          target1: data.target1 || '',
          target2: data.target2 || '',
          target3: data.target3 || '',
          target1_update: data.target1_update || '',
          target2_update: data.target2_update || '',
          target3_update: data.target3_update || '',
          target1_achieved: data.target1_achieved || false,
          target2_achieved: data.target2_achieved || false,
          target3_achieved: data.target3_achieved || false,
          comments: data.comments || '',
          created_by: data.created_by || '',
          created_by_uid: data.created_by_uid || '',
          created_by_email: data.created_by_email || '',
          updated_at: updatedAt,
          created_at: createdAt,
          kind: summary.kind,
          origin: summary.origin,
          activity_at: summary.activityAt
        });
      });

      setClientSuccessMetrics(metrics);
    } catch (error) {
      console.error('Error fetching client success metrics:', error);
      enqueueSnackbar('Failed to load success metrics', { variant: 'error' });
    } finally {
      setIsLoadingSuccessMetrics(false);
    }
  }, [clientId, enqueueSnackbar]);
  // Fetch the initial goal from client_intake or health_consult
const fetchInitialGoal = useCallback(async () => {
  if (!clientId) return;
  
  try {
    // First try to get goal from client_intake
    const intakeDocRef = doc(db, `client_intake/${clientId}`);
    const intakeDocSnap = await getDoc(intakeDocRef);
    
    if (intakeDocSnap.exists() && intakeDocSnap.data().your_goal) {
      const goal = intakeDocSnap.data().your_goal;
      if (goal && typeof goal === 'string' && goal.trim() !== '') {
        setInitialGoal(goal);
        return;
      }
    }
    
    // If not found or empty, try health_consult
    const healthConsultDocRef = doc(db, `health_consult/${clientId}`);
    const healthConsultDocSnap = await getDoc(healthConsultDocRef);
    
    if (healthConsultDocSnap.exists() && healthConsultDocSnap.data().final_agreed_goal) {
      const goal = healthConsultDocSnap.data().final_agreed_goal;
      if (goal && typeof goal === 'string' && goal.trim() !== '') {
        setInitialGoal(goal);
        return;
      }
    }
    
    // If no goal found in either collection
    setInitialGoal(null);
  } catch (error) {
    console.error('Error fetching initial goal:', error);
    setInitialGoal(null);
  }
}, [clientId]);

// Call this function in useEffect where you fetch client data
useEffect(() => {
  fetchInitialGoal();
}, [fetchInitialGoal]);

  useEffect(() => {
    fetchClientSuccessMetrics();
  }, [fetchClientSuccessMetrics]);

  // Assessment targets are a read-only suggestion for the first update's dialog (viewing never writes)
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setAssessmentSuggestion(null);
    getDocs(query(
      collection(db, 'bigquery_cache/client_data/assessment_data'),
      where('mindbody_client_id', '==', clientId)
    ))
      .then((snapshot) => {
        if (cancelled) return;
        setAssessmentSuggestion(suggestTargetsFromAssessments(
          snapshot.docs.map((assessmentDoc) => ({ id: assessmentDoc.id, data: assessmentDoc.data() }))
        ));
      })
      .catch((error) => {
        console.error('Error fetching assessment targets for success suggestion:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Handle opening the success metric dialog
  const handleOpenSuccessMetricDialog = (metricId?: string) => {
    if (metricId) {
      // Edit existing metric
      const metric = clientSuccessMetrics.find(m => m.id === metricId);
      if (metric) {
        setSelectedMetricId(metricId);
        setSuccessMetricGoal(metric.goal || '');
        setSuccessMetricGoalUpdate(metric.goal_update || '');
        setSuccessMetricGoalAchieved(metric.goal_achieved || false);
        setSuccessMetricTarget1(metric.target1);
        setSuccessMetricTarget2(metric.target2);
        setSuccessMetricTarget3(metric.target3);
        setSuccessMetricTarget1Update(metric.target1_update);
        setSuccessMetricTarget2Update(metric.target2_update);
        setSuccessMetricTarget3Update(metric.target3_update);
        setSuccessMetricTarget1Achieved(metric.target1_achieved);
        setSuccessMetricTarget2Achieved(metric.target2_achieved);
        setSuccessMetricTarget3Achieved(metric.target3_achieved);
        // Never show (or save back) the legacy placeholder text, whatever the record's origin
        setSuccessMetricComments(metric.comments === LEGACY_PLANNED_COMMENT ? '' : metric.comments);
      }
    } else {
      // New metric - initialize with existing targets if available
      setSelectedMetricId(null);
      
      // If there are previous metrics, use their targets as defaults
      if (clientSuccessMetrics.length > 0) {
        const latestMetric = clientSuccessMetrics[0];
        setSuccessMetricGoal(latestMetric.goal || initialGoal || '');
        setSuccessMetricTarget1(latestMetric.target1);
        setSuccessMetricTarget2(latestMetric.target2);
        setSuccessMetricTarget3(latestMetric.target3);
      } else {
        // Default values for new client, pre-filled from assessment targets when available
        setSuccessMetricGoal(initialGoal || 'Set Client Goal');
        setSuccessMetricTarget1(assessmentSuggestion?.target1 || 'Set Target 1');
        setSuccessMetricTarget2(assessmentSuggestion?.target2 || 'Set Target 2');
        setSuccessMetricTarget3(assessmentSuggestion?.target3 || 'Set Target 3');
      }
      
      // Clear update fields
      setSuccessMetricGoalUpdate('');
      setSuccessMetricGoalAchieved(false);
      setSuccessMetricTarget1Update('');
      setSuccessMetricTarget2Update('');
      setSuccessMetricTarget3Update('');
      setSuccessMetricTarget1Achieved(false);
      setSuccessMetricTarget2Achieved(false);
      setSuccessMetricTarget3Achieved(false);
      setSuccessMetricComments('');
    }
    
    setShowSuccessMetricDialog(true);
  };
  
  // Handle closing the success metric dialog
  const handleCloseSuccessMetricDialog = () => {
    setShowSuccessMetricDialog(false);
    setSelectedMetricId(null);
    setSuccessMetricGoal('');
    setSuccessMetricGoalUpdate('');
    setSuccessMetricGoalAchieved(false);
    setSuccessMetricTarget1('');
    setSuccessMetricTarget2('');
    setSuccessMetricTarget3('');
    setSuccessMetricTarget1Update('');
    setSuccessMetricTarget2Update('');
    setSuccessMetricTarget3Update('');
    setSuccessMetricTarget1Achieved(false);
    setSuccessMetricTarget2Achieved(false);
    setSuccessMetricTarget3Achieved(false);
    setSuccessMetricComments('');
  };

  const handleSaveSuccessMetric = async () => {
    if (!clientId || !user) return;
    
    setIsSubmittingSuccessMetric(true);
    
    try {
      const form: SuccessMetricForm = {
        goal: successMetricGoal,
        goal_update: successMetricGoalUpdate,
        goal_achieved: successMetricGoalAchieved,
        target1: successMetricTarget1,
        target2: successMetricTarget2,
        target3: successMetricTarget3,
        target1_update: successMetricTarget1Update,
        target2_update: successMetricTarget2Update,
        target3_update: successMetricTarget3Update,
        target1_achieved: successMetricTarget1Achieved,
        target2_achieved: successMetricTarget2Achieved,
        target3_achieved: successMetricTarget3Achieved,
        comments: successMetricComments,
      };
      const author: SuccessMetricAuthor = {
        created_by: user.displayName && user.displayName.trim() !== '' 
          ? user.displayName 
          : user.email || 'Unknown User',
        created_by_uid: user.uid,
        created_by_email: user.email || '',
      };
      
      // Check if we have any new goals/targets to create a follow-up metric
      const hasNewGoalsOrTargets = nextGoal || nextTarget1 || nextTarget2 || nextTarget3;
      
      if (selectedMetricId) {
        // Update existing metric. Filling a planned placeholder or assessment seed turns it into
        // an update and removes the seed's stored `id` field instead of adding another record.
        const selectedMetric = clientSuccessMetrics.find(m => m.id === selectedMetricId);
        const payload = selectedMetric && selectedMetric.origin !== 'update'
          ? buildFillPayload(clientId, form, author, deleteField())
          : buildUpdatePayload(clientId, form, author);
        await updateDoc(doc(db, 'client_success', selectedMetricId), {
          ...payload,
          updated_at: serverTimestamp(),
        });
        enqueueSnackbar('Success metric updated', { variant: 'success' });
      } else {
        // Create new metric
        await addDoc(collection(db, 'client_success'), {
          ...buildUpdatePayload(clientId, form, author),
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
        enqueueSnackbar('Success metric added', { variant: 'success' });
      }
      
      // If we have new goals/targets, create a planned record (the next-update slot) with those values
      if (hasNewGoalsOrTargets) {
        // Use new goal/targets if available, otherwise keep the current ones
        await addDoc(collection(db, 'client_success'), {
          ...buildPlannedPayload(clientId, {
            goal: nextGoal || successMetricGoal,
            target1: nextTarget1 || successMetricTarget1,
            target2: nextTarget2 || successMetricTarget2,
            target3: nextTarget3 || successMetricTarget3,
          }, author),
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
        enqueueSnackbar('New success metric created with updated goals/targets', { variant: 'success' });
        
        // Clear the next goals/targets
        setNextGoal('');
        setNextTarget1('');
        setNextTarget2('');
        setNextTarget3('');
      }
      
      handleCloseSuccessMetricDialog();
      fetchClientSuccessMetrics();
    } catch (error) {
      console.error('Error saving success metric:', error);
      enqueueSnackbar('Failed to save success metric', { variant: 'error' });
    } finally {
      setIsSubmittingSuccessMetric(false);
    }
  };

  // State to track new goals/targets for future metrics
  const [nextGoal, setNextGoal] = useState('');
  const [nextTarget1, setNextTarget1] = useState('');
  const [nextTarget2, setNextTarget2] = useState('');
  const [nextTarget3, setNextTarget3] = useState('');

  // Handlers for new target dialogs
  const handleNewGoalSubmit = () => {
    if (newGoalValue.trim()) {
      // Format the update to show the achieved goal and the new goal
      const achievedGoal = successMetricGoal;
      const newUpdateText = `NEW GOAL: ${newGoalValue}`;
      
      // Append to existing update text instead of replacing it
      const existingUpdate = successMetricGoalUpdate.trim();
      const formattedUpdate = existingUpdate 
        ? `${existingUpdate}\n\n${newUpdateText}` 
        : newUpdateText;
      
      // Keep the original goal as is (showing it was achieved)
      // Add the new goal info to the update field
      setSuccessMetricGoalUpdate(formattedUpdate);
      
      // Store the new goal for future metrics
      setNextGoal(newGoalValue);
    }
    setShowNewGoalDialog(false);
  };

  const handleNewTarget1Submit = () => {
    if (newTarget1Value.trim()) {
      // Format the update to show the achieved target and the new target
      const achievedTarget = successMetricTarget1;
      const newUpdateText = `NEW TARGET: ${newTarget1Value}`;
      
      // Append to existing update text instead of replacing it
      const existingUpdate = successMetricTarget1Update.trim();
      const formattedUpdate = existingUpdate 
        ? `${existingUpdate}\n\n${newUpdateText}` 
        : newUpdateText;
      
      // Keep the original target as is (showing it was achieved)
      // Add the new target info to the update field
      setSuccessMetricTarget1Update(formattedUpdate);
      
      // Store the new target for future metrics
      setNextTarget1(newTarget1Value);
    }
    setShowNewTarget1Dialog(false);
  };

  const handleNewTarget2Submit = () => {
    if (newTarget2Value.trim()) {
      // Format the update to show the achieved target and the new target
      const achievedTarget = successMetricTarget2;
      const newUpdateText = `\n\nNEW TARGET: ${newTarget2Value}`;
      
      // Append to existing update text instead of replacing it
      const existingUpdate = successMetricTarget2Update.trim();
      const formattedUpdate = existingUpdate 
        ? `${existingUpdate}\n\n${newUpdateText}` 
        : newUpdateText;
      
      // Keep the original target as is (showing it was achieved)
      // Add the new target info to the update field
      setSuccessMetricTarget2Update(formattedUpdate);
      
      // Store the new target for future metrics
      setNextTarget2(newTarget2Value);
    }
    setShowNewTarget2Dialog(false);
  };

  const handleNewTarget3Submit = () => {
    if (newTarget3Value.trim()) {
      // Format the update to show the achieved target and the new target
      const achievedTarget = successMetricTarget3;
      const newUpdateText = `\n\nNEW TARGET: ${newTarget3Value}`;
      
      // Append to existing update text instead of replacing it
      const existingUpdate = successMetricTarget3Update.trim();
      const formattedUpdate = existingUpdate 
        ? `${existingUpdate}\n\n${newUpdateText}` 
        : newUpdateText;
      
      // Keep the original target as is (showing it was achieved)
      // Add the new target info to the update field
      setSuccessMetricTarget3Update(formattedUpdate);
      
      // Store the new target for future metrics
      setNextTarget3(newTarget3Value);
    }
    setShowNewTarget3Dialog(false);
  };

  // Adapt the fetched metrics to the shared success-record helpers (kind + activityAt + createdAt)
  const records = useMemo(
    () => clientSuccessMetrics.map(m => ({ ...m, activityAt: m.activity_at, createdAt: toDate(m.created_at) })),
    [clientSuccessMetrics]
  );
  // Due 42 days after the latest real update, or after the first session when there is none.
  // First session falls back to the earliest valid visit date, as on the Dashboard and /success.
  const nextSuccessReportDue = useMemo(() => {
    let firstSession = toDate(client?.first_session_date);
    if (!firstSession) {
      const visitDates = (client?.visit_dates ?? [])
        .map(d => toDate(d))
        .filter((d): d is Date => d !== null);
      firstSession = visitDates.length > 0
        ? new Date(Math.min(...visitDates.map(d => d.getTime())))
        : null;
    }
    return nextSuccessUpdateDue(records, firstSession);
  }, [records, client?.first_session_date, client?.visit_dates]);
  const isSuccessReportDue = nextSuccessReportDue !== null && nextSuccessReportDue <= new Date();

  // Latest = newest real update; Next = the newest record when it is still a blank placeholder or seed
  const latestSuccessMetric = latestCountedRecord(records);
  const nextPlanned = findFillableRecord(records);
  const legacySuccessMetrics = records.filter(
    m => m.id !== latestSuccessMetric?.id && m.id !== nextPlanned?.id
  );
  const metricsCount = records.filter(m => m.kind === 'update').length;

  // The legacy placeholder text is never shown as a comment
  const visibleComments = (comments: string): string => (comments === LEGACY_PLANNED_COMMENT ? '' : comments);

  const getLatestUpdateDate = (metric: ClientSuccessMetric): Date | null => {
    const createdAtDate = toDate(metric.created_at);
    const updatedAtDate = toDate(metric.updated_at);

    if (!createdAtDate || !updatedAtDate) return null;
    if (updatedAtDate.getTime() <= createdAtDate.getTime()) return null;

    return differenceInMinutes(updatedAtDate, createdAtDate) >= 60 ? updatedAtDate : null;
  };

  const renderTargetCard = (
    label: string,
    value: string,
    update: string,
    achieved: boolean,
    key: string
  ) => (
    <Grid item xs={12} md={4} key={key}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value || 'Not set'}
        </Typography>
        {update && (
          <Typography variant="body2" color="text.secondary">
            Update: {update}
          </Typography>
        )}
        <Chip
          icon={achieved ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
          label={achieved ? 'Achieved' : 'In progress'}
          color={achieved ? 'success' : 'default'}
          variant={achieved ? 'filled' : 'outlined'}
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        />
      </Paper>
    </Grid>
  );

  const latestMetricCreatedAt = latestSuccessMetric ? toDate(latestSuccessMetric.created_at) : null;
  const latestMetricUpdateDate = latestSuccessMetric ? getLatestUpdateDate(latestSuccessMetric) : null;
  const now = new Date();
  const daysUntilNextReport = nextSuccessReportDue ? differenceInCalendarDays(nextSuccessReportDue, now) : null;
  let reportStatusLabel = 'No report scheduled';
  let reportStatusColor: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
  let reportStatusHelper = metricsCount === 0
    ? 'Log a success metric to establish the client baseline.'
    : 'Set a cadence to keep success reports on track.';
  const formattedNextReportDate = nextSuccessReportDue
    ? formatDateSafe(nextSuccessReportDue, 'PPP', 'Unknown date')
    : 'Not scheduled';

  if (daysUntilNextReport !== null) {
    const dayLabel = Math.abs(daysUntilNextReport) === 1 ? 'day' : 'days';
    if (daysUntilNextReport < 0) {
      reportStatusLabel = `Overdue by ${Math.abs(daysUntilNextReport)} ${dayLabel}`;
      reportStatusColor = 'error';
      reportStatusHelper = `The last success report was due on ${formattedNextReportDate}.`;
    } else if (daysUntilNextReport === 0) {
      reportStatusLabel = 'Due today';
      reportStatusColor = 'warning';
      reportStatusHelper = 'Complete the success report today to stay on track.';
    } else if (daysUntilNextReport <= 7) {
      reportStatusLabel = `Due in ${daysUntilNextReport} ${dayLabel}`;
      reportStatusColor = 'warning';
      reportStatusHelper = `Plan to submit the success report by ${formattedNextReportDate}.`;
    } else {
      reportStatusLabel = `Due in ${daysUntilNextReport} ${dayLabel}`;
      reportStatusColor = 'success';
      reportStatusHelper = `Next success report expected on ${formattedNextReportDate}.`;
    }
  }

  const lastTouchDate = latestMetricUpdateDate || latestMetricCreatedAt;
  const lastTouchDescriptor = lastTouchDate ? formatDistanceToNow(lastTouchDate, { addSuffix: true }) : null;
  const metricsCountLabel = metricsCount === 0
    ? 'No updates logged yet'
    : `${metricsCount} update${metricsCount > 1 ? 's' : ''} logged`;

  useEffect(() => {
    if (legacySuccessMetrics.length === 0 && legacyMetricsExpanded) {
      setLegacyMetricsExpanded(false);
    }
  }, [legacySuccessMetrics.length, legacyMetricsExpanded]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!client) {
    return (
      <Box sx={{ textAlign: 'center', my: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Client not found
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/clients')}
          sx={{ mt: 2 }}
        >
          Return to Clients
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Premium Header Component */}
      <ClientDetailsHeader
        client={client}
        onCopyClientId={() => {
          enqueueSnackbar('Client ID copied to clipboard', { variant: 'success' });
        }}
      />
      
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="client details tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" />
            <Tab label="Sessions" />
            <Tab label="Check-ins" />
            <Tab label="Notes" />
            <Tab label="Assessments" />
            <Tab label="Reports" />
            <Tab label="Success" />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <ClientJourneyOverview
              client={client}
              milestones={getSortedTimelineMilestones()}
              assessmentData={{
                preHealthConsult: client.pre_health_consult_data_submitted || false,
                healthConsult: client.health_consult_data_submitted || false,
                intakeForm: client.client_intake_data_submitted || false,
                initialReports: {
                  inbody: isReportSubmitted('inbody', 'Initial'),
                  movement: isReportSubmitted('movement', 'Initial'),
                  metabolic: isReportSubmitted('metabolic', 'Initial'),
                  nutrition: isReportSubmitted('nutrition', 'Initial'),
                  training: isReportSubmitted('training', 'Initial')
                },
                reviewReports: {
                  inbody: isReportSubmitted('inbody', 'Review'),
                  movement: isReportSubmitted('movement', 'Review'),
                  metabolic: isReportSubmitted('metabolic', 'Review'),
                  nutrition: isReportSubmitted('nutrition', 'Review'),
                  training: isReportSubmitted('training', 'Review')
                }
              }}
              onGenerateReport={generateAssessmentReport}
              upcomingAppointments={client.upcoming_appointments}
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <ClientSessionsView
              client={client}
              upcomingAppointments={client.upcoming_appointments}
              sessionHistory={client.visit_history || []}
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <ClientCheckInsView
              client={client}
              checkIns={clientCheckIns}
              npsData={npsData}
              isLoading={isLoadingCheckIns}
              userRole={userRole}
              onAddCheckIn={(type) => {
                setCheckInType(type);
                setCheckInResponse('');
                setSelectedCheckInId(null);
                setShowCheckInDialog(true);
              }}
              onEditCheckIn={(checkIn) => {
                setCheckInResponse(checkIn.response);
                setSelectedCheckInId(checkIn.id);
                setCheckInType(checkIn.check_in_type || '2_week');
                setShowCheckInDialog(true);
              }}
              onDeleteCheckIn={(checkInId) => {
                setCheckInToDelete(checkInId);
                setShowDeleteConfirmDialog(true);
              }}
            />
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <ClientNotesView
              client={client}
              notes={clientNotes.map(note => ({
                ...note,
                created_by_name: note.created_by
              }))}
              isLoading={isLoadingNotes}
              userRole={userRole}
              currentUserId={user?.uid || ''}
              currentUserName={user?.displayName || user?.email || ''}
              onAddNote={async (noteData) => {
                try {
                  const newNote = {
                    client_id: clientId,
                    content: noteData.content || '',
                    created_by: user?.displayName || user?.email || 'Unknown',
                    created_by_uid: user?.uid || '',
                    created_by_email: user?.email || '',
                    created_at: serverTimestamp(),
                    updated_at: serverTimestamp(),
                    is_pinned: noteData.is_pinned || false,
                    tags: noteData.tags || [],
                    category: noteData.category || 'general',
                    color: noteData.color || 'default',
                    is_private: noteData.is_private || false,
                    is_management_only: noteData.is_management_only || false,
                    is_important: noteData.is_important || false,
                    mentions: noteData.mentions || [],
                    action_items: noteData.action_items || []
                  };
                  
                  await addDoc(collection(db, 'client_notes'), newNote);
                  enqueueSnackbar('Note added successfully', { variant: 'success' });
                  fetchClientNotes();
                } catch (error) {
                  console.error('Error adding note:', error);
                  enqueueSnackbar('Failed to add note', { variant: 'error' });
                }
              }}
              onEditNote={async (noteId, updates) => {
                try {
                  const noteRef = doc(db, 'client_notes', noteId);
                  await updateDoc(noteRef, {
                    ...updates,
                    last_edited_at: serverTimestamp(),
                    last_edited_by: user?.displayName || user?.email || 'Unknown'
                  });
                  enqueueSnackbar('Note updated successfully', { variant: 'success' });
                  fetchClientNotes();
                } catch (error) {
                  console.error('Error updating note:', error);
                  enqueueSnackbar('Failed to update note', { variant: 'error' });
                }
              }}
              onDeleteNote={async (noteId) => {
                try {
                  await deleteDoc(doc(db, 'client_notes', noteId));
                  enqueueSnackbar('Note deleted successfully', { variant: 'success' });
                  fetchClientNotes();
                } catch (error) {
                  console.error('Error deleting note:', error);
                  enqueueSnackbar('Failed to delete note', { variant: 'error' });
                }
              }}
              onTogglePin={handleToggleNotePin}
              onToggleArchive={async (noteId, isArchived) => {
                try {
                  const noteRef = doc(db, 'client_notes', noteId);
                  await updateDoc(noteRef, {
                    is_archived: isArchived
                  });
                  enqueueSnackbar(isArchived ? 'Note archived' : 'Note unarchived', { variant: 'success' });
                  fetchClientNotes();
                } catch (error) {
                  console.error('Error toggling archive:', error);
                  enqueueSnackbar('Failed to update note', { variant: 'error' });
                }
              }}
              coaches={collaboratingCoaches}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <AssessmentSection
              clientId={clientId || ''}
              currentProgramme={client?.current_programme || ''}
              userRole={userRole || ''}
              initialGoal={initialGoal || undefined}
            />
              
              {reportSubmissions.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <EnhancedAssessmentMetrics
                    assessmentData={extractAssessmentMetrics()}
                    isLoading={false}
                    clientId={clientId || ''}
                    clientName={client.client_name || 'Client'}
                    rawAssessmentData={reportSubmissions}
                  />
                </Box>
              )}
              
              
                  {/* No Assessment Data Message */}
                  {reportSubmissions.length === 0 && (
                    <Grid item xs={12}>
                      <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No Assessment Data Available
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          Complete the required assessments to view metrics and progress charts.
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
          </TabPanel>
          
          <TabPanel value={tabValue} index={5}>
            <Typography variant="h6" gutterBottom>
              Reports
            </Typography>
            
            {isLoadingReports ? (
              <LoadingSpinner minHeight="100px" />
            ) : generatedReports.length > 0 ? (
              <List>
                {generatedReports.map((report) => (
                  <Paper 
                    key={report.id}
                    elevation={1}
                    sx={{ 
                      p: 2, 
                      mb: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Typography variant="subtitle1">
                          {reportRowLabel(report.report_type, report.report_category)}
                          {report.report_category === 'lactate_threshold' && (
                            <Chip
                              size="small"
                              label="Lactate"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem', borderColor: '#AB292C', color: '#AB292C' }}
                              variant="outlined"
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            if (!report.generated_at) return 'No date available';
                            const date = toDate(report.generated_at);
                            return formatDateSafe(date, 'MMMM dd, yyyy', 'No date available');
                          })()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <Typography variant="body2">
                          <strong>Status:</strong> {report.report_status || 'Generated'}
                          {report.is_fallback && (
                            <Chip 
                              size="small" 
                              label="Fallback" 
                              color="warning" 
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Typography>
                        {report.generated_by ? (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Generated by:</strong> {report.generated_by.name || report.generated_by.email || 'Unknown'}
                          </Typography>
                        ) : report.latest_report?.generated_by ? (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Generated by:</strong> {report.latest_report.generated_by}
                          </Typography>
                        ) : null}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            component="a"
                            href={report.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            View
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            component="a"
                            href={report.download_url || report.url}
                            download
                            size="small"
                          >
                            Download
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </List>
            ) : (
              <Alert severity="info">
                No reports available for this client.
              </Alert>
            )}
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                startIcon={<AssessmentIcon />}
                onClick={() => setShowReportDialog(true)}
                disabled={isGeneratingReport}
              >
                Generate New Report
              </Button>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={6}>
            <Box sx={{ mt: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Paper
                  elevation={0}
                  sx={(theme) => ({
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: theme.palette.mode === 'light'
                      ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(33, 150, 243, 0) 60%)'
                      : 'linear-gradient(135deg, rgba(144, 202, 249, 0.2) 0%, rgba(13, 71, 161, 0.1) 60%)'
                  })}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="overline"
                        color="primary.main"
                        sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}
                      >
                        Client success
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="h5">
                          Client Success Metrics
                        </Typography>
                        {isSuccessReportDue && (
                          <Chip
                            icon={<NotificationsActiveIcon />}
                            label="Success Report Due"
                            color="error"
                            size="small"
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {reportStatusHelper}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                        justifyContent="flex-end"
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
                        >
                          <Chip
                            icon={<NotificationsActiveIcon />}
                            label={reportStatusLabel}
                            color={reportStatusColor}
                            variant={reportStatusColor === 'success' ? 'filled' : 'outlined'}
                            size="small"
                            sx={{ whiteSpace: 'nowrap' }}
                          />
                          {lastTouchDescriptor && (
                            <Chip
                              icon={<UpdateIcon />}
                              label={`Last touch ${lastTouchDescriptor}`}
                              color="info"
                              variant="outlined"
                              size="small"
                              sx={{ whiteSpace: 'nowrap' }}
                            />
                          )}
                          {metricsCount > 0 && (
                            <Chip
                              icon={<AssessmentIcon />}
                              label={metricsCountLabel}
                              color="info"
                              variant="outlined"
                              size="small"
                              sx={{ whiteSpace: 'nowrap' }}
                            />
                          )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Next success report due: {formattedNextReportDate}
                        </Typography>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start', md: 'flex-end' } }}
                        >
                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<TrophyIcon />}
                            onClick={() => setShowNominationDialog(true)}
                          >
                            Submit Nomination
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenSuccessMetricDialog(nextPlanned ? nextPlanned.id : undefined)}
                          >
                            {nextPlanned ? 'Record update' : 'Add Success Metric'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
              
              {nominationScope && (
                <Box sx={{ mb: 3 }}>
                  <ClientNominationsSummary
                    nominations={clientNominations}
                    isLoading={isLoadingNominations}
                    error={nominationsError}
                    scopeRole={nominationScope.role}
                    onViewAll={() => navigate(`/success-nominations?q=${encodeURIComponent(clientId || '')}`)}
                  />
                </Box>
              )}

              {isLoadingSuccessMetrics ? (
                <LoadingSpinner minHeight="100px" />
              ) : clientSuccessMetrics.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No success metrics recorded yet.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Click the button above to add the first success metric for this client.
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {latestSuccessMetric && (
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: latestSuccessMetric.goal_achieved ? 'success.light' : 'divider',
                        backgroundColor: latestSuccessMetric.goal_achieved ? 'rgba(76, 175, 80, 0.08)' : 'background.paper'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: 2
                        }}
                      >
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            Latest Success Metric
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Recorded on {formatDateSafe(latestMetricCreatedAt, 'PPP', 'Unknown date')} by {latestSuccessMetric.created_by}
                          </Typography>
                          {latestMetricUpdateDate && (
                            <Chip
                              icon={<UpdateIcon />}
                              label={`Latest update ${formatDateSafe(latestMetricUpdateDate, 'PPP p', 'Unknown time')}`}
                              color="info"
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                          <Chip
                            icon={latestSuccessMetric.goal_achieved ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                            label={latestSuccessMetric.goal_achieved ? 'Goal achieved' : 'Goal in progress'}
                            color={latestSuccessMetric.goal_achieved ? 'success' : 'warning'}
                            size="small"
                          />
                          {legacySuccessMetrics.length > 0 && (
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<HistoryIcon />}
                              onClick={() => setLegacyMetricsExpanded(true)}
                            >
                              View history
                            </Button>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {latestSuccessMetric.goal || 'Goal not set'}
                        </Typography>
                        {latestSuccessMetric.goal_update && (
                          <Box
                            sx={{
                              mt: 1,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5
                            }}
                          >
                            <Chip
                              icon={<UpdateIcon />}
                              label="Goal update"
                              color="info"
                              size="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                              {latestSuccessMetric.goal_update}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {renderTargetCard(
                          'Target 1',
                          latestSuccessMetric.target1,
                          latestSuccessMetric.target1_update,
                          latestSuccessMetric.target1_achieved,
                          `${latestSuccessMetric.id}-target1`
                        )}
                        {renderTargetCard(
                          'Target 2',
                          latestSuccessMetric.target2,
                          latestSuccessMetric.target2_update,
                          latestSuccessMetric.target2_achieved,
                          `${latestSuccessMetric.id}-target2`
                        )}
                        {renderTargetCard(
                          'Target 3',
                          latestSuccessMetric.target3,
                          latestSuccessMetric.target3_update,
                          latestSuccessMetric.target3_achieved,
                          `${latestSuccessMetric.id}-target3`
                        )}
                      </Grid>

                      {visibleComments(latestSuccessMetric.comments) && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Comments
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {visibleComments(latestSuccessMetric.comments)}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenSuccessMetricDialog(latestSuccessMetric.id)}
                        >
                          Edit
                        </Button>
                      </Box>
                    </Paper>
                  )}

                  {nextPlanned && (
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        borderWidth: 1,
                        borderStyle: 'dashed',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Next goal & targets
                        </Typography>
                        {!latestSuccessMetric && (
                          <Typography variant="caption" color="text.secondary" component="p">
                            No update recorded yet
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Set on {formatDateSafe(nextPlanned.createdAt, 'PPP', 'Unknown date')}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {nextPlanned.goal || 'Goal not set'}
                        </Typography>
                      </Box>

                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        {[nextPlanned.target1, nextPlanned.target2, nextPlanned.target3].map((target, index) => (
                          <Grid item xs={12} md={4} key={`${nextPlanned.id}-target${index + 1}`}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Target {index + 1}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {target || 'Not set'}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>

                      {visibleComments(nextPlanned.comments) && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Comments
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {visibleComments(nextPlanned.comments)}
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenSuccessMetricDialog(nextPlanned.id)}
                        >
                          Record update
                        </Button>
                      </Box>
                    </Paper>
                  )}

                  {legacySuccessMetrics.length > 0 && (
                    <Accordion
                      expanded={legacyMetricsExpanded}
                      onChange={(_, expanded) => setLegacyMetricsExpanded(expanded)}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle1">
                            Success Metric History ({legacySuccessMetrics.length})
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Expand to review previous goals and targets
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {legacySuccessMetrics.map((metric) => {
                            const createdAtDate = toDate(metric.created_at);
                            const latestUpdateDate = getLatestUpdateDate(metric);

                            return (
                              <Paper
                                key={metric.id}
                                variant="outlined"
                                sx={{ p: 3, borderRadius: 2 }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 2
                                  }}
                                >
                                  <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      Recorded on {formatDateSafe(createdAtDate, 'PPP', 'Unknown date')} by {metric.created_by}
                                    </Typography>
                                    {latestUpdateDate && (
                                      <Chip
                                        icon={<UpdateIcon />}
                                        label={`Latest update ${formatDateSafe(latestUpdateDate, 'PPP p', 'Unknown time')}`}
                                        color="info"
                                        size="small"
                                        sx={{ mt: 1 }}
                                      />
                                    )}
                                    {metric.kind !== 'update' && (
                                      <Chip
                                        label="No update recorded"
                                        size="small"
                                        variant="outlined"
                                        sx={{ mt: 1, ml: latestUpdateDate ? 1 : 0 }}
                                      />
                                    )}
                                  </Box>
                                  <Chip
                                    icon={metric.goal_achieved ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                                    label={metric.goal_achieved ? 'Goal achieved' : 'Goal in progress'}
                                    color={metric.goal_achieved ? 'success' : 'warning'}
                                    size="small"
                                  />
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {metric.goal || 'Goal not set'}
                                  </Typography>
                                  {metric.goal_update && (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5
                                      }}
                                    >
                                      <Chip
                                        icon={<UpdateIcon />}
                                        label="Goal update"
                                        color="info"
                                        size="small"
                                      />
                                      <Typography variant="body2" color="text.secondary">
                                        {metric.goal_update}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                  {renderTargetCard(
                                    'Target 1',
                                    metric.target1,
                                    metric.target1_update,
                                    metric.target1_achieved,
                                    `${metric.id}-target1`
                                  )}
                                  {renderTargetCard(
                                    'Target 2',
                                    metric.target2,
                                    metric.target2_update,
                                    metric.target2_achieved,
                                    `${metric.id}-target2`
                                  )}
                                  {renderTargetCard(
                                    'Target 3',
                                    metric.target3,
                                    metric.target3_update,
                                    metric.target3_achieved,
                                    `${metric.id}-target3`
                                  )}
                                </Grid>

                                {visibleComments(metric.comments) && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Comments
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {visibleComments(metric.comments)}
                                    </Typography>
                                  </Box>
                                )}

                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleOpenSuccessMetricDialog(metric.id)}
                                  >
                                    Edit
                                  </Button>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              )}
            </Box>
          </TabPanel>
        </Box>
      </Paper>
      
      {/* Submit Nomination Dialog */}
      {client && (
        <SubmitNominationDialog
          open={showNominationDialog}
          onClose={() => setShowNominationDialog(false)}
          client={client}
          currentGoal={clientSuccessMetrics[0]?.goal || initialGoal}
          recentNominations={clientNominations}
          onSubmitted={handleNominationSubmitted}
        />
      )}

      {/* Check-in Dialog */}
      <Dialog 
        open={showCheckInDialog} 
        onClose={() => {
          setShowCheckInDialog(false);
          setCheckInResponse('');
          setSelectedCheckInId(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCheckInId 
            ? `Edit ${checkInType === '2_week' ? '2-Week' : '10-Week'} Check-in` 
            : `New ${checkInType === '2_week' ? '2-Week' : '10-Week'} Check-in`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Client: {client?.client_name || 'N/A'}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {checkInType === '2_week' 
                ? 'This check-in is typically conducted 2 weeks after the client starts their program.'
                : 'This check-in is typically conducted 10 weeks after the client starts their program.'}
            </Typography>
            
            <TextField
              label="Check-in Notes"
              multiline
              rows={6}
              value={checkInResponse}
              onChange={(e) => setCheckInResponse(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="Enter detailed notes about the client's progress, challenges, and any adjustments to their program..."
              error={checkInResponse.trim() === ''}
              helperText={checkInResponse.trim() === '' ? 'Check-in notes are required' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowCheckInDialog(false);
              setCheckInResponse('');
              setSelectedCheckInId(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCheckInSubmit}
            disabled={checkInResponse.trim() === ''}
          >
            {selectedCheckInId ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteConfirmDialog} 
        onClose={() => {
          setShowDeleteConfirmDialog(false);
          setCheckInToDelete(null);
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            <Typography variant="body1">
              Are you sure you want to delete this check-in?
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The check-in data will be permanently removed from the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowDeleteConfirmDialog(false);
              setCheckInToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={() => {
              if (checkInToDelete) {
                handleCheckInDelete(checkInToDelete);
                setShowDeleteConfirmDialog(false);
                setCheckInToDelete(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Report Generation Dialog */}
      <Dialog 
        open={showReportDialog} 
        onClose={() => {
          if (!isGeneratingReport) {
            setShowReportDialog(false);
            setReportGenerationProgress(0);
            setGeneratedReportUrl('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Assessment Report</DialogTitle>
        <DialogContent>
          {isGeneratingReport ? (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Generating report... This may take 2-3 minutes.
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={reportGenerationProgress} 
                sx={{ mt: 1, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {reportGenerationProgress < 30 ? 'Collecting client data...' : 
                 reportGenerationProgress < 50 ? 'Preparing assessment information...' : 
                 reportGenerationProgress < 70 ? 'Generating report document...' :
                 reportGenerationProgress < 85 ? 'Formatting and finalizing...' :
                 reportGenerationProgress < 90 ? 'Almost complete...' :
                 reportGenerationProgress === 100 ? 'Report generated!' : 'Finalizing report...'}
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1, display: 'block' }}>
                {reportGenerationProgress}% complete
              </Typography>
              {reportGenerationProgress === 100 && generatedReportUrl && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Report generated successfully!
                </Alert>
              )}
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Select the type of assessment report to generate:
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => generateAssessmentReport('Initial')}
                  disabled={!areAllReportsSubmitted('Initial')}
                >
                  Initial Assessment Report
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => generateAssessmentReport('Review')}
                  disabled={!areAllReportsSubmitted('Review')}
                >
                  Review Assessment Report
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowReportDialog(false);
              setReportGenerationProgress(0);
              setGeneratedReportUrl('');
            }}
            disabled={isGeneratingReport}
          >
            {generatedReportUrl ? 'Close' : 'Cancel'}
          </Button>
          {generatedReportUrl && (
            <Button 
              variant="contained" 
              component="a"
              href={generatedReportUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<VisibilityIcon />}
            >
              View Report
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Report Success Dialog */}
<Dialog 
  open={showReportSuccessDialog} 
  onClose={() => {
    setShowReportSuccessDialog(false);
    setReportGenerationProgress(0);
    setGeneratedReportUrl('');
  }}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>Report Generated Successfully</DialogTitle>
  <DialogContent>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
      <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        Your report is ready!
      </Typography>
      <Typography variant="body1" align="center" sx={{ mb: 3 }}>
        The {client?.client_name} assessment report has been successfully generated and is ready to view or download.
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2, mt: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<VisibilityIcon />}
          component="a"
          href={generatedReportUrl}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
        >
          View Report
        </Button>
        
        <Button
          variant="outlined"
          color="primary"
          startIcon={<GetAppIcon />}
          component="a"
          href={`https://drive.google.com/uc?export=download&id=${extractFileIdFromUrl(generatedReportUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
        >
          Download Report
        </Button>
      </Box>
    </Box>
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={() => {
        setShowReportSuccessDialog(false);
        setReportGenerationProgress(0);
      }}
    >
      Close
    </Button>
  </DialogActions>
</Dialog>
      {/* Success Metric Dialog */}
      <Dialog 
        open={showSuccessMetricDialog} 
        onClose={handleCloseSuccessMetricDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMetricId 
            ? `Edit Success Metric` 
            : `New Success Metric`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Client: {client?.client_name || 'N/A'}
            </Typography>
            {/* Goal Section */}
<Grid item xs={12}>
  <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1, color: 'primary.main' }}>
    Main Goal
  </Typography>
</Grid>
<Grid item xs={12} sm={8}>
  <TextField
    label="Client Goal"
    value={successMetricGoal}
    onChange={(e) => setSuccessMetricGoal(e.target.value)}
    fullWidth
    placeholder="Enter client's main goal..."
    error={successMetricGoal.trim() === ''}
    helperText={successMetricGoal.trim() === '' ? 'Goal is required' : ''}
  />
</Grid>
<Grid item xs={12} sm={4}>
  <FormControlLabel
    control={
      <Checkbox
        checked={successMetricGoalAchieved}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setSuccessMetricGoalAchieved(e.target.checked);
          if (e.target.checked) {
            setNewGoalValue('');
            setShowNewGoalDialog(true);
          }
        }}
      />
    }
    label="Goal Achieved"
  />
</Grid>
<Grid item xs={12}>
  <TextField
    label="Goal Update"
    value={successMetricGoalUpdate}
    onChange={(e) => setSuccessMetricGoalUpdate(e.target.value)}
    fullWidth
    multiline
    rows={2}
    placeholder="Enter update for client's goal..."
  />
</Grid>
            <Grid container spacing={3}>
              {/* Target 1 Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                  Target 1
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Target 1"
                  value={successMetricTarget1}
                  onChange={(e) => setSuccessMetricTarget1(e.target.value)}
                  fullWidth
                  placeholder="Enter target 1..."
                  error={successMetricTarget1.trim() === ''}
                  helperText={successMetricTarget1.trim() === '' ? 'Target 1 is required' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={successMetricTarget1Achieved}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSuccessMetricTarget1Achieved(e.target.checked);
                        if (e.target.checked) {
                          setNewTarget1Value('');
                          setShowNewTarget1Dialog(true);
                        }
                      }}
                    />
                  }
                  label="Target Achieved"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Target 1 Update"
                  value={successMetricTarget1Update}
                  onChange={(e) => setSuccessMetricTarget1Update(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter update for target 1..."
                />
              </Grid>
              
              {/* Target 2 Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                  Target 2
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Target 2"
                  value={successMetricTarget2}
                  onChange={(e) => setSuccessMetricTarget2(e.target.value)}
                  fullWidth
                  placeholder="Enter target 2..."
                  error={successMetricTarget2.trim() === ''}
                  helperText={successMetricTarget2.trim() === '' ? 'Target 2 is required' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={successMetricTarget2Achieved}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSuccessMetricTarget2Achieved(e.target.checked);
                        if (e.target.checked) {
                          setNewTarget2Value('');
                          setShowNewTarget2Dialog(true);
                        }
                      }}
                    />
                  }
                  label="Target Achieved"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Target 2 Update"
                  value={successMetricTarget2Update}
                  onChange={(e) => setSuccessMetricTarget2Update(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter update for target 2..."
                />
              </Grid>
              
              {/* Target 3 Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                  Target 3
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Target 3"
                  value={successMetricTarget3}
                  onChange={(e) => setSuccessMetricTarget3(e.target.value)}
                  fullWidth
                  placeholder="Enter target 3..."
                  error={successMetricTarget3.trim() === ''}
                  helperText={successMetricTarget3.trim() === '' ? 'Target 3 is required' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={successMetricTarget3Achieved}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSuccessMetricTarget3Achieved(e.target.checked);
                        if (e.target.checked) {
                          setNewTarget3Value('');
                          setShowNewTarget3Dialog(true);
                        }
                      }}
                    />
                  }
                  label="Target Achieved"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Target 3 Update"
                  value={successMetricTarget3Update}
                  onChange={(e) => setSuccessMetricTarget3Update(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter update for target 3..."
                />
              </Grid>
              
              {/* Comments Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, borderBottom: '1px solid #eee', pb: 1 }}>
                  Additional Comments
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Comments"
                  value={successMetricComments}
                  onChange={(e) => setSuccessMetricComments(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter any additional comments..."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseSuccessMetricDialog}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveSuccessMetric}
            disabled={
              successMetricTarget1.trim() === '' || 
              successMetricTarget2.trim() === '' || 
              successMetricTarget3.trim() === ''
            }
          >
            {selectedMetricId ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Goal Dialog */}
      <Dialog
        open={showNewGoalDialog}
        onClose={() => setShowNewGoalDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set New Goal</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Current goal has been achieved. Please set a new goal for the client.
            </Typography>
            <TextField
              label="New Goal"
              value={newGoalValue}
              onChange={(e) => setNewGoalValue(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter new goal..."
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewGoalDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleNewGoalSubmit}
            disabled={!newGoalValue.trim()}
          >
            Set New Goal
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Target 1 Dialog */}
      <Dialog
        open={showNewTarget1Dialog}
        onClose={() => setShowNewTarget1Dialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set New Target 1</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Target 1 has been achieved. Please set a new target.
            </Typography>
            <TextField
              label="New Target 1"
              value={newTarget1Value}
              onChange={(e) => setNewTarget1Value(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter new target..."
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewTarget1Dialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleNewTarget1Submit}
            disabled={!newTarget1Value.trim()}
          >
            Set New Target
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Target 2 Dialog */}
      <Dialog
        open={showNewTarget2Dialog}
        onClose={() => setShowNewTarget2Dialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set New Target 2</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Target 2 has been achieved. Please set a new target.
            </Typography>
            <TextField
              label="New Target 2"
              value={newTarget2Value}
              onChange={(e) => setNewTarget2Value(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter new target..."
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewTarget2Dialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleNewTarget2Submit}
            disabled={!newTarget2Value.trim()}
          >
            Set New Target
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Target 3 Dialog */}
      <Dialog
        open={showNewTarget3Dialog}
        onClose={() => setShowNewTarget3Dialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set New Target 3</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Target 3 has been achieved. Please set a new target.
            </Typography>
            <TextField
              label="New Target 3"
              value={newTarget3Value}
              onChange={(e) => setNewTarget3Value(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Enter new target..."
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewTarget3Dialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleNewTarget3Submit}
            disabled={!newTarget3Value.trim()}
          >
            Set New Target
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDetails;
